import { FIGURE_SVG_EDITOR } from '../../utils/roles';
import MathName from '../SyllabusV2/MathName';
/**
 * ProblemViewTab — problem-hub 우측 [조회] 탭 (③ 문제 상세 + 편집).
 *
 * 계약: BE-20260825-problem-hub-integrated.md §3 — GET /api/v2/problems/{id} 하나로
 * 본문·정답·풀이·개념·난이도·출신을 받는다. 본문/풀이는 Lexical editorState 문자열이라
 * 기존 렌더러(OcrLexicalEditor)로 그대로 그린다 — "답풀이 조회 화면의 정본".
 *
 * 편집: BE-20260825-fe-handover.md §B-1 — PATCH /api/v2/problems/{id} 한 번으로
 * 본문·정답·풀이를 함께 저장한다(보낸 키만 반영). OCR 검수와 같은 단일 저장 계약이고,
 * 다른 점은 여기가 **은행 문제를 직접** 고친다는 것뿐이다(스테이징이 아니다).
 * 개념·난이도도 2026-09-13 부터 여기서 고친다 — 같은 PATCH 에 concept_ids·difficulty 를 싣는다
 * (요청서 FE-20260913 §2-1 → 회신 BE-20260913 · dev 279bbed). 비우기는 없다(null·[] 는 400 이라 보내기 전에 막는다).
 *   옛 서버(279bbed 전)는 두 키를 400 으로 거절하고, 그러면 같은 요청의 본문 저장까지 막힌다. 그래서 상세에
 *   `labels` 배열이 없으면(같은 커밋이 실었다) 두 칸을 잠근다 — canEditMeta(오너 결정 2026-09-13 B).
 * 라벨은 2026-09-13 부터 여기서 고친다 — 사전 API(/api/v2/labels)로 떼고·만들고·붙인다(labelEdit · applyLabelPlan).
 *   [저장] 한 번이 「문제 PATCH → 라벨 쓰기 → 상세 다시 읽기」 순서로 돈다. 라벨 쓰기가 updated_at 을 올려서,
 *   거꾸로 하면 PATCH 가 409 stale 을 맞는다.
 * 되돌리기는 [수정 이력]이 맡는다 — 라벨 판도 그 탭에 선다.
 *
 * 2026-08-26 갱신: BE-20260826-audit-fixes-folder-tree-v2apis.md
 *   §3 낙관락이 세졌다 — 409 에 `data.reason:'stale'` 이 실리고, **분석 승인·본문 되돌리기가
 *     `Problems.updated_at` 을 전진시킨다.** 그 둘 뒤에는 다시 읽어야 저장할 수 있다.
 *   §4 D2 승인된 객관식 정답이 `'3'` 이 아니라 원문자 `'③'` 로 저장된다(이 화면의 관례와 같다).
 *
 * ⚠ Lexical 편집분을 지키는 네 조각(lexKey·keepLexSnapshot·readLexEdit·flushLexBeforeSwitch)은
 *   OcrHub/OcrReviewModal.jsx 의 것을 **의도적으로 옮겨 왔다**. 공용 훅으로 빼면 살아 있는
 *   검수 화면(2100줄)까지 함께 흔들리는데, 그 화면은 지금 매일 쓰인다. 중복을 감수하고
 *   이 파일 안에서 닫는다. 고칠 일이 생기면 두 곳을 함께 본다.
 *   없으면 편집분이 사라진다 — Lexical 은 언마운트하며 root 를 비워 스냅샷이 늘 빈 문서로
 *   읽힌다(그쪽 주석에 실측 90바이트로 기록돼 있다).
 *
 * ⚠ ESC: OcrLexicalEditor 는 자기 영역의 ESC 를 삼킨다(모달 닫힘과 편집 조작이 겹치지 않게).
 *   이 탭에는 ESC 를 듣는 것이 원래 없어 손해가 없고, 새로 붙인 미리보기만 ESC 로 닫는다 —
 *   그 리스너는 document 에 있고, 미리보기가 떠 있는 동안 커서는 편집기 밖(포털 안)이라 닿는다.
 * ⚠ z-index: 미리보기 포털 9999960 < 편집기 툴바 드롭다운 9999990. 겹치지 않는 이유는
 *   드롭다운이 document click(capture)로 닫히기 때문이다 — 미리보기를 여는 클릭이 먼저 닫는다.
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, Suspense, lazy } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Eye, Save, RotateCcw, ImagePlus } from 'lucide-react';
import { Button, MathceptDetailModal } from '../common';
import { useAuth } from '../../context/AuthContext';
import OcrLexicalEditor, { normalizeEditorState } from '../OcrHub/OcrLexicalEditor';
import FigureModal from '../FigureEditor/FigureModal';
// [이미지 추가]의 갈래 고르기와 AI 생성 창 — 검수 모달과 **같은 창**을 쓴다(오너 지시 2026-09-05
// 「어떤 경로든 동일 기능·동일 모달」). 생성은 재요청 창(FigureReconvertModal)의 kind='generate' 얼굴이다.
import FigureAddModal from '../FigureEditor/FigureAddModal';
import FigureReconvertModal from '../FigureEditor/FigureReconvertModal';
// 붙여넣기·파일 추가가 「바꿀 그림」이 있을 때 묻는 창 — 검수 모달과 같은 창이다. 2026-09-05 부터 은행도
// `PATCH {image}` 교체를 받아(계약 §10-2 G1) 같은 갈래(새 그림으로 넣기 / N번째 바꾸기)를 낸다.
import OcrPasteImageModal from '../OcrHub/OcrPasteImageModal';
import { collectFigureNodes } from '../FigureEditor/figureMatch';
// 그림 생성(계약 §9-2, 2026-08-30) — 그림이 **없는** 자리에 만드는 길이라 그림 창(FigureModal)이
// 아니라 여기에 붙는다. 그 창은 「그림 하나를 다루는 창」이고, 여기에는 아직 그 하나가 없다.
// purgeRemovedFigures — 편집기에서 손으로 지운 그림을 **저장 전에** 삭제 API 로 돌린다(2026-09-04).
// fetchFigures·reconvertFigure — 올린 이미지로 생성할 때(편집기에 넣고 저장한 뒤 그 그림을 재요청) 쓴다.
import {
  generateFigure, purgeRemovedFigures, fetchFigures, reconvertFigure, parseImageDataUri,
} from '../FigureEditor/figureApi';
import {
  describeFigureError, FIGURE_NOT_GENERATED_TEXT, figureRedrawnText, figureRedrawRejectedText,
  describePurgeFailure, describePartialPurge, describeImageReject,
} from '../FigureEditor/figureText';
// 생성 창에 낼 「이 자리 그림들의 원본」 카드(2026-09-06 A 안) — 검수 모달과 같은 규칙으로 짓는다.
import { listFigureOrigins } from '../FigureEditor/figureOrigins';
// 정답 한 줄(18px·KaTeX) — 분석 카드와 함께 쓰려고 파일로 꺼냈다(2026-09-27)
import AnswerView from './AnswerView';
import { CIRCLED } from '../OcrHub/BlockRenderer';
// 개념 배지와 v2 사전·상세 — 시험지 쪽(PaperItemsEditor·PaperStatsPanel)과 **같은 부품**이다.
// 개념을 그리는 얼굴이 화면마다 갈리면 같은 값을 다른 것으로 읽게 된다.
import { ConceptBadge, matchConcept, fetchConceptDictV2, fetchConceptDetailV2 } from '../ExamPapersV2/conceptBadge';
import { LabelBadges, splitLabels } from '../ExamPapersV2/labelBadge';
// 문제 번호는 표와 같은 정본 얼굴이다 — 고정폭·굵게·12px, 색이 곧 상태(완비 초록 · 빌 것 있음 먹색), 누르면 그 문제로.
// 머리의 「원본」 번호도 2026-09-11 부터 칩이 아니라 이 얼굴로 선다(오너 지시).
import ProblemIdLink, { isModifiedClick } from './ProblemIdLink';
// 「OCR 등록」 배지 — 누르면 그 문제를 만든 OCR 문제의 검수 모달이 이 자리에 뜬다(회신 BE-20261002-reply-problem-ocr-origin).
// 꾸밈 클릭(⌘·Ctrl)은 그 검수 화면 주소를 새 탭으로 연다 — 주소 규칙은 읽는 쪽(OCR 화면)과 한 파일을 나눠 쓴다.
import { ocrReviewPathOf } from '../OcrHub/ocrReviewLink';
import OcrSourceReviewModal from './OcrSourceReviewModal';
import {
  fetchProblem, updateProblem, searchProblems,
  getOriginDisplay, DIFFICULTY_LABEL, difficultyBadgeClass, difficultyOptionLabel, AUTH_REDIRECT,
  fetchProblemLabels, searchLabels, applyLabelPlan,
} from './problemApi';
// 개념 칸 — 편집 모드는 공용 개념 고르기(오너 결정 2026-09-13 D4 — OCR 검수 창과 같은 부품).
import ConceptPicker from './ConceptPicker';
// 라벨 칸 — 칩·입력칸은 검수 창과 **같은 부품**, 초안 계산은 순수 함수 묶음(labelEdit.test 가 못박는다).
import LabelEditor from './LabelEditor';
import {
  EMPTY_LABEL_DRAFT, labelChips, labelPlan, isLabelPlanEmpty, addLabelTokens, removeLabelChip,
  describeLabelError, describeRejectedLabels,
} from './labelEdit';

// 지면 모양 미리보기는 자기 포털·자기 API·KaTeX 를 다 끌고 온다 — 열어 볼 때만 받는다.
// v2 전용 미리보기 — 이 탭은 이미 v2 상세(GET/PATCH /api/v2/problems/{id})로 돌아간다.
const ProblemPreviewV2 = lazy(() => import('../ExamPapersV2/ProblemPreviewV2'));

// answer 비교 정규화 — 빈 문자열은 null(정답 비우기)로 본다. 양쪽에 같은 규칙을 써야
// 무편집이 dirty 로 오탐되지 않고, 공백만 친 것이 answer:null 로 나가지 않는다.
const normAnswer = (v) => {
  const s = v == null ? '' : String(v);
  return s.trim() === '' ? null : s;
};

// 개념 목록 비교 — 순서는 보지 않는다. 서버가 order 내림차순으로 다시 세우므로(회신 BE-20260913 §2)
// 순서로 재면 저장 직후에도 「바뀜」으로 읽힌다.
const sameIdSet = (a, b) => {
  const key = (x) => [...(x || [])].map(Number).sort((p, q) => p - q).join(',');
  return key(a) === key(b);
};

// 풀이가 아예 없는 문제에 처음 쓸 때의 빈 문서. 계약(§B-1)은 solution_lexical 을 upsert 하지만
// 편집기는 root.children 이 빈 값을 열지 못한다(Lexical error #38) — 문단 하나를 놓아 준다.
// 사용자가 실제로 쓰지 않으면 dirty 가 아니라 저장에도 실리지 않는다.
const EMPTY_LEXICAL = JSON.stringify({
  root: {
    children: [{ children: [], direction: null, format: '', indent: 0, type: 'paragraph', version: 1 }],
    direction: null, format: '', indent: 0, type: 'root', version: 1,
  },
});

/**
 * 실제로 구르는 조상. 이 탭은 문제 상세의 칸(ProblemHub — 두 칸이면 왼쪽 칸, 한 단이면 상세 칸.
 * 둘 다 `overflow-y-auto`) 안에서 구른다 — 문서가 아니라 그 칸의 scrollTop 을 옮겨야 화면이 따라온다.
 * 가장 가까운 구르는 조상을 찾으므로 칸이 바뀌어도 이 함수는 그대로다. 못 찾으면 문서가 구르는 것으로 본다.
 */
function scrollableAncestor(el) {
  for (let n = el.parentElement; n; n = n.parentElement) {
    const oy = window.getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
  }
  return null;
}

/**
 * 정답 입력 — OcrReviewModal 의 AnswerField 와 같은 규칙이다(본문 편집기의 수식 노드와도 같다):
 * 평소에는 KaTeX 로 그리고, 누르면 LaTeX 원문으로 바뀐다. 저장 값은 언제나 그 문자열 그대로라
 * 계약이 걸리지 않는다 — 편집기를 붙이는 것이 아니라 같은 문자열을 다르게 보여 주는 것뿐이다.
 * (계약에 있는 editorState 는 body·solution 둘뿐이고 answer 는 한 줄 문자열이다.)
 */
function AnswerField({ value, onChange, editing, setEditing, disabled }) {
  const inputRef = useRef(null);
  const enteredWith = useRef(''); // ESC 로 되돌릴 값 — 편집에 들어간 순간의 원문

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const open = () => {
    if (disabled) return;
    enteredWith.current = value;
    setEditing(true);
  };

  // 고치는 중이면 disabled 여도 입력칸을 유지한다 — 저장하는 몇 초 동안 보기 상자로 바뀌었다가
  // 실패해서 되돌아오면 칸이 두 번 갈아엎히고 고치던 자리도 잃는다.
  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        onBlur={() => setEditing(false)} // 벗어나면 확정 — 값은 이미 부모가 쥐고 있다
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            setEditing(false);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            onChange(enteredWith.current);
            setEditing(false);
          }
        }}
        placeholder="정답 없음 — 비운 채로 저장하면 정답이 지워집니다"
        className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
      />
    );
  }

  return (
    // 보기 상자는 입력칸과 같은 치수를 쓴다 — 다르면 오갈 때마다 아래가 흔들린다
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      /* aria-label 은 내용(AnswerView)을 덮는다 — 값을 이름에 담지 않으면 정답이 아예 안 읽힌다 */
      aria-label={`정답 ${String(value ?? '').trim() || '없음'} — 눌러서 수정`}
      title={disabled ? undefined : '눌러서 수정'}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          open();
        }
      }}
      className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-sm ${
        disabled ? 'bg-gray-50 text-gray-400' : 'cursor-text hover:border-primary/50 focus:border-primary focus:outline-none'
      }`}
    >
      <span className="min-w-0 flex-1"><AnswerView value={value} /></span>
    </div>
  );
}

/**
 * @param canRunAi [이미지 추가](본문·풀이)를 세울지 — 그 창의 「AI 생성」 갈래가 LLM 비용을 낸다.
 *   서버는 강사에게도 열려 있고(회신 BE-20260910 §1-1 「그림 편집 전부」) 화면에서만 감춘다 —
 *   [분석] 탭·[변형] 탭·목록과 같은 축(utils/roles.js AI_RUN_ROLES), 판정은 담는 쪽(pages/ProblemHub.js)이 한다.
 *   비용이 없는 「이미지 파일 추가」 갈래까지 함께 사라진다 — 오너가 버튼째 감추기로 정했다(2026-09-10).
 *   붙여넣기·드롭은 이 버튼을 거치지 않고 이미지 교체(replaceFigureImage)만 부르므로 그대로 둔다.
 *   기본값이 허용(true)인 것은 형제 부품과 같은 이유다.
 * @param canEditFigures 편집기 안 그림을 눌렀을 때 뜨는 버튼 넷(편집·AI 요청·이미지 교체·지우기)을 세울지.
 *   강사에게는 **크기 조절 손잡이만** 남긴다(오너 지시 2026-09-10 · roles.js FIGURE_EDIT_ROLES).
 *   canRunAi 와 따로 받는 이유는 그 상수의 머리주석에 있다 — 값이 같아도 축이 다르다.
 *   감추는 방식은 FigureEditContext 규약대로 `onAction` 을 주지 않는 것이다. 그러면 편집기가 막대를 그리지 않고,
 *   Delete 키로 지운 그림은 종전대로 편집기가 노드를 빼고 [저장]이 서버에서 지운다(purgeRemovedFigures).
 *   그림 창(FigureModal)은 그 막대로만 열리므로 따로 막지 않는다.
 * @param canOpenOcr 머리의 「OCR 등록」 배지를 눌러 그 문제의 OCR 검수 모달을 띄울지(utils/roles.js OCR_ROLES).
 *   번호 두 칸은 교사에게도 실려 오지만(회신 §1) OCR API·화면(/ocr-hub)이 매니저 전용이라 교사에게는 열 수 없다.
 *   형제 props 와 달리 기본값이 닫힘(false)인 이유가 그것이다 — 넘기는 쪽이 판정하지 않으면 배지를 글자로 둔다.
 */
function ProblemViewTab({
  problemId, refreshKey = 0, onOpenProblem = null,
  onDirtyChange = null, onChanged = null, onLoaded = null,
  canRunAi = true, canEditFigures = true, canOpenOcr = false,
  // [보기|편집] 전환을 그려 넣을 자리(DOM 요소). 주면 그 자리로(createPortal), 안 주면 종전대로 본문 줄에 선다.
  // 두 칸 배치의 머리 줄이 이것을 준다(pages/ProblemHub) — 전환은 본문만이 아니라 이 칸 전체의 상태라서다.
  modeSlot = null,
}) {
  const { getAccessToken } = useAuth();
  const [data, setData] = useState(null);
  // 「OCR 등록」 배지가 연 OCR 검수 모달 — 이 화면을 떠나지 않으므로 미저장 편집을 묻지 않는다
  const [ocrReviewOpen, setOcrReviewOpen] = useState(false);
  // 개념 배지가 [순번][유형]을 얻는 사전(v2). 모듈 캐시(5분)라 이 탭이 처음 부르는 값이어도
  // 왕복은 세션당 한 번에 그친다 — 검색 패널·시험지 창과 같은 사전을 나눠 쓴다.
  const [conceptDict, setConceptDict] = useState(null);
  const [conceptDetail, setConceptDetail] = useState(null); // { loading, data } — 설명 창
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const targetRef = useRef(problemId);
  const seqRef = useRef(0);
  useEffect(() => { targetRef.current = problemId; }, [problemId]);

  // ── 편집 ──
  const [mode, setMode] = useState('view'); // 'view' 보기 | 'edit' 편집
  const [answerText, setAnswerText] = useState('');
  const [answerEditing, setAnswerEditing] = useState(false);
  // 개념·난이도 초안 — seed 가 서버 값으로 채운다. 난이도는 select 값이라 문자열('' = 값 없음).
  const [conceptIds, setConceptIds] = useState([]);
  const [difficulty, setDifficulty] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [stale, setStale] = useState(false); // 409 — 내가 읽은 뒤 서버 쪽이 바뀌었다
  const [solutionDraft, setSolutionDraft] = useState(false); // 풀이가 없는 문제에 새로 쓰는 중
  const [previewOpen, setPreviewOpen] = useState(false);

  const bodyEditorRef = useRef(null);
  const solutionEditorRef = useRef(null);
  const bodyLexPendingRef = useRef(null);
  const solutionLexPendingRef = useRef(null);
  const [bodyDirty, setBodyDirty] = useState(false);
  const [solDirty, setSolDirty] = useState(false);
  const [lexResetSeq, setLexResetSeq] = useState(0); // [편집 취소]·[새로 읽기]가 편집기를 다시 세우는 손잡이

  // 그림 창(BE-20260827) — { action, field, index, src } | null.
  // 열려 있는 동안 뒤 편집기를 잠근다(오너 결정 2026-08-28 「모달이 닫혀야만 다시 수정」).
  const [figureTarget, setFigureTarget] = useState(null);
  // 그림 생성(계약 §9-2) — 만들 자리('body'|'solution') | null. 이 값이 곧 창의 열림 여부다.
  const [figGenField, setFigGenField] = useState(null);
  const [figGenBusy, setFigGenBusy] = useState(false);
  /**
   * 생성 창의 「이 자리 그림들의 원본」 카드(2026-09-06 A 안) — `{ loading, items, token }`. 창을 열 때 목록
   * (GET …/figures)을 읽어 짓고(figureOrigins), `token` 은 그 응답의 updated_at — 원본 카드로 재요청할 때의 낙관락.
   */
  const [figGenOrigins, setFigGenOrigins] = useState({ loading: false, items: [], token: null });
  const figGenSeqRef = useRef(0); // 창을 닫았다 다른 자리로 다시 열었을 때 늦게 온 목록이 덮지 않게
  // [이미지 추가]가 여는 갈래 고르기 — 만들 자리('body'|'solution') | null. 검수 모달과 같은 창(FigureAddModal).
  const [figAddField, setFigAddField] = useState(null);
  // [이미지 파일 추가]의 파일 고르기 — 고른 파일은 편집기에 바로 들어가고 [저장]이 반영한다(은행은 저장이 곧 목록).
  const imageInputRef = useRef(null);
  const imageInputFieldRef = useRef('body');
  // 편집기에 들어오려다 **가로채인** 이미지 — 붙여넣기 창이 뜬 동안 여기 머문다(검수 모달과 같은 모양).
  const [pasteTarget, setPasteTarget] = useState(null);
  // 들어오는 이미지의 입구(takeIncomingImage) — 의존성 없는 콜백(handlePasteImage)이 최신 것을 읽는 통로
  const takeIncomingRef = useRef(null);
  // 그림 작업 직전의 자동 저장(ensureSaved) — 의존성 없는 콜백(handleFigureAction·openFigGen)이 최신 것을 읽는 통로
  const ensureSavedRef = useRef(null);

  // ── 라벨(v2 사전) ──
  // 사전 번호(label_uuid)는 상세에 없어(labels_raw 문자열뿐 — 요청서 §2-3) **편집 모드에서만** 따로 읽는다.
  // forId: 어느 문제의 목록인지 — 문제를 넘긴 직후 앞 문제의 칩이 비치지 않게 대조한다.
  const [labelServer, setLabelServer] = useState({ status: 'idle', items: [], forId: null, error: null });
  const [labelDraft, setLabelDraft] = useState(EMPTY_LABEL_DRAFT);
  const [labelInput, setLabelInput] = useState('');
  const labelSeqRef = useRef(0);
  // 라벨 저장이 끝났다 — 다음에 도착하는 목록과 **같은 박자에** 초안을 비운다. 먼저 비우면 새 목록이
  // 오기 전 한 박자 동안 뗀 라벨이 되살아나고 넣은 라벨이 사라져 보인다.
  const clearLabelDraftOnLoadRef = useRef(false);

  const seed = useCallback((d) => {
    setAnswerText(d?.answer ?? '');
    setAnswerEditing(false);
    setConceptIds(Array.isArray(d?.concept_ids) ? d.concept_ids : []);
    setDifficulty(d?.difficulty != null ? String(d.difficulty) : '');
    setBodyDirty(false);
    setSolDirty(false);
    setSolutionDraft(false);
    setStale(false);
    // 새 상세를 seed 하면 이전 편집 스냅샷은 무효(키가 어긋나 어차피 안 쓰이지만 명시적으로 비운다)
    bodyLexPendingRef.current = null;
    solutionLexPendingRef.current = null;
  }, []);

  const load = useCallback(async () => {
    if (problemId == null) return;
    const target = problemId;
    // 같은 문제로도 두 번 겹친다(승인 두 번 → refreshKey 두 번) — 늦게 온 옛 응답이 새 내용을
    // 덮지 않도록 순번으로도 거른다.
    const seq = (seqRef.current += 1);
    setLoading(true);
    setError(null);
    try {
      const d = await fetchProblem(target, getAccessToken);
      if (seq !== seqRef.current || String(target) !== String(targetRef.current)) return;
      setData(d);
      onLoadedRef.current?.(d); // 머리의 번호가 상태 색을 입으려면 부모가 상세를 알아야 한다
      seed(d);
    } catch (e) {
      if (seq !== seqRef.current || String(target) !== String(targetRef.current)) return;
      setError(e.message || '문제를 불러오지 못했습니다.');
      setData(null);
      onLoadedRef.current?.(null);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [problemId, getAccessToken, seed]);

  useEffect(() => { load(); }, [load]);

  // 개념 사전 — 배지의 [순번][유형] 재료다. 못 받으면 null 이고, 그때 배지는 이름만 남는다
  // (ConceptBadge 가 order·type 없으면 그 칸을 안 그린다). 화면이 나빠지지 않으므로 따로 알리지 않는다.
  useEffect(() => {
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken)).then((d) => { if (alive) setConceptDict(d); });
    return () => { alive = false; };
  }, [getAccessToken]);
  // 개념 번호 → 사전 항목 — 폐기(is_outdated) 판정과 이름 폴백에 쓴다
  const dictById = useMemo(() => new Map((conceptDict || []).map((c) => [String(c.id ?? c.concept_id), c])), [conceptDict]);

  const editingNow = mode === 'edit';
  const loadedId = data?.problem_id ?? null;
  const labelsRawNow = data?.labels_raw ?? null;
  const loadLabels = useCallback(async () => {
    if (loadedId == null) return;
    const target = loadedId;
    const seq = (labelSeqRef.current += 1);
    setLabelServer((s) => ({ ...s, status: 'loading', error: null }));
    try {
      const d = await fetchProblemLabels(target, getAccessToken);
      if (seq !== labelSeqRef.current || String(target) !== String(targetRef.current)) return;
      setLabelServer({ status: 'ready', items: Array.isArray(d?.labels) ? d.labels : [], forId: target, error: null });
      if (clearLabelDraftOnLoadRef.current) {
        clearLabelDraftOnLoadRef.current = false;
        setLabelDraft(EMPTY_LABEL_DRAFT);
      }
    } catch (e) {
      if (seq !== labelSeqRef.current || String(target) !== String(targetRef.current)) return;
      setLabelServer({ status: 'error', items: [], forId: target, error: e.message || '라벨을 불러오지 못했습니다.' });
    }
  }, [loadedId, getAccessToken]);
  // 상세가 라벨 목록(사전 번호 포함)을 싣는가 — dev 279bbed(회신 BE-20260913 §1)부터 싣는다. 실으면 따로 읽지 않는다.
  const detailHasLabels = Array.isArray(data?.labels);
  // 옛 서버일 때만: 편집 모드에 들어오면 읽고, 라벨 문자열이 바뀌면(내 저장 · 다른 곳의 변경을 다시 읽음) 또 읽는다.
  useEffect(() => {
    if (editingNow && loadedId != null && !detailHasLabels) loadLabels();
  }, [editingNow, loadedId, labelsRawNow, detailHasLabels, loadLabels]);
  // 다른 문제로 넘어가면 초안을 버린다 — 넘어가기 전에 이탈 가드가 이미 물었다.
  useEffect(() => {
    setLabelDraft(EMPTY_LABEL_DRAFT);
    setLabelInput('');
    clearLabelDraftOnLoadRef.current = false;
  }, [problemId]);
  // 사전 후보 — 부품이 이 함수의 정체성으로 다시 찾을지 가르므로 고정해 둔다
  const suggestLabels = useCallback((q) => searchLabels(q, getAccessToken), [getAccessToken]);

  /**
   * 원본 번호의 색 재료 — 번호가 곧 상태다(problemApi.problemIdTone). 그 판정에 드는 need_* 네 플래그는
   * **검색 응답에만** 실려서(ProblemIdLink 주석) 상세에는 없다. 그래서 원본 한 건만 검색으로 받는다(본문 없이).
   * 못 받으면 item 없이 먹색으로 선다 — 모르는 것을 초록으로 말하지 않는다.
   * 원본이 있는 문제(AI 변형)에서만 한 번 더 부른다.
   */
  const originId = data?.origin_parent_id ?? null;
  const [originItem, setOriginItem] = useState(null);
  useEffect(() => {
    setOriginItem(null);
    if (originId == null) return undefined;
    let alive = true;
    searchProblems({ problemIds: [originId], perPage: 5 }, getAccessToken)
      .then((r) => {
        if (!alive) return;
        const hit = (r?.items || []).find((it) => String(it.problem_id) === String(originId));
        setOriginItem(hit || null);
      })
      .catch(() => { /* 색만 모르는 것이다 — 번호와 이동은 그대로 선다 */ });
    return () => { alive = false; };
  }, [originId, getAccessToken]);

  /**
   * 개념 배지를 누르면 그 개념의 **설명**을 띄운다(오너 지시 2026-08-29).
   * 종전에는 「이 개념의 다른 문제 찾기」로 목록을 좁혔다 — 같은 배지에 두 뜻을 담을 수 없어
   * 설명 쪽을 골랐다. (PaperItemsEditor 의 openConcept 와 같은 규약이다.)
   * 실패하면 **빈 창을 띄우지 않는다** — 못 받았는데 창만 열리면 「설명이 없는 개념」으로 읽힌다.
   */
  const openConcept = useCallback(async (conceptId) => {
    if (conceptId == null) return;
    setConceptDetail({ loading: true, data: [] });
    const list = await fetchConceptDetailV2([conceptId], getAccessToken);
    if (list == null) {
      setConceptDetail(null);
      setToast({ type: 'error', text: '개념 설명을 불러오지 못했습니다.' });
      return;
    }
    setConceptDetail({ loading: false, data: list });
  }, [getAccessToken]);

  // ── Lexical 편집분을 지키는 네 조각 (OcrReviewModal 과 동형) ──

  // ① 편집기 인스턴스 키 = 문제 + 서버 버전(+ 되세우기 카운터). 저장으로 updated_at 이 바뀌면
  //    리마운트되고, 그 이전 스냅샷은 키 불일치로 자동 폐기된다(낡은 편집이 새 서버 상태를
  //    덮지 않게). lexResetSeq 가 없으면 [편집 취소]에 값이 그대로라 리마운트가 일어나지 않아
  //    "되돌렸다"면서 편집기 안 글자는 남는다.
  const lexKey = (prefix) => `${prefix}:${data?.problem_id ?? ''}:${data?.updated_at ?? ''}:${lexResetSeq}`;

  // root 에 자식이 하나도 없는 editorState — Lexical 이 언마운트하며 남기는 껍데기의 모양이다.
  const isEmptyEditorState = (s) => {
    try { return (JSON.parse(String(s))?.root?.children || []).length === 0; } catch (_) { return false; }
  };

  // ② 편집기 언마운트(모드 전환·문제 이동) 시 스냅샷 보관. '열 수 없는 편집'은 보관하지 않는다 —
  //    보관해 봐야 저장 경로(readLexEdit)가 걸러 내므로 배지만 '수정됨'으로 남아 가드와 어긋난다.
  const keepLexSnapshot = (label, snap, pendingRef, key, setDirty) => {
    // 전환 직전에 flushLexBeforeSwitch 가 이미 판정했으면 그것이 정본이다. 언마운트 순간의
    // editorState 는 믿을 수 없다 — Lexical 이 플러그인을 정리하며 root 를 비운 뒤라 늘 빈 문서다.
    const kept = pendingRef.current;
    if (kept?.flushed && kept.key === key) {
      kept.flushed = false;
      return;
    }
    const usable = snap?.dirty ? normalizeEditorState(snap.state) : null;
    if (usable) {
      pendingRef.current = { state: usable, dirty: true, key };
      return;
    }
    pendingRef.current = null;
    setDirty(false);
    // 진짜로 비운 경우만 말한다 — 위 실측 때문에 조건 없이 알리면 전환마다 거짓 경고가 뜬다.
    if (snap?.dirty && !isEmptyEditorState(snap.state)) {
      setToast({ type: 'error', text: `${label}을 완전히 비울 수는 없어 저장본으로 되돌렸습니다.` });
    }
  };

  // ③ 마운트 중이면 라이브 상태가 정본(서버 값 대비 dirty 일 때만), 언마운트 상태면 스냅샷.
  //    편집이 없으면 null — 저장 patch 에서 빠진다("바뀐 것만 보낸다").
  const readLexEdit = (ref, pendingRef, key) => {
    // 라이브 편집기는 '이 문제의 것'일 때만 정본이다. 문제를 넘긴 직후의 렌더에서는 이전 문제의
    // 편집기가 아직 언마운트되지 않아, key 를 안 보면 남의 본문이 새 문제 화면으로 딸려 온다.
    if (ref.current && ref.current.key === key) {
      return ref.current.isDirty() ? ref.current.getState() : null;
    }
    const p = pendingRef.current;
    // 스냅샷은 열 수 있는 형태일 때만 쓴다 — 마운트가 서버 값으로 폴백한 상황에서 저장만 깨진
    // 스냅샷을 보내면 화면과 저장물이 갈린다.
    if (p && p.key === key && p.dirty) return normalizeEditorState(p.state);
    return null;
  };

  // 지금 이 문제의 '정본' 본문/풀이 — 미저장 편집분이 있으면 그것, 없으면 서버 값.
  // 보기 모드도 이 값을 그린다. 모드마다 다른 원본을 보면 화면과 저장물이 갈린다.
  const lexNow = (ref, pendingRef, key, serverValue) =>
    readLexEdit(ref, pendingRef, key) ?? serverValue ?? null;

  // ④ 모드를 떠나기 '전에' 편집기 상태를 스냅샷으로 밀어 넣는다. 언마운트 cleanup 은 새 모드가
  //    렌더된 뒤에 돌기 때문에, 그것만 믿으면 보기 모드가 한 박자 늦은 내용을 그린다.
  //    flushed 표시를 남겨 뒤이어 도는 cleanup 이 이 판정을 빈 문서로 덮지 않게 한다.
  const flushLexBeforeSwitch = (label, ref, pendingRef, key, setDirty) => {
    const st = ref.current;
    if (!st || st.key !== key) return;
    let usable = null;
    let dirty = false;
    try {
      dirty = st.isDirty();
      usable = dirty ? normalizeEditorState(st.getState()) : null;
    } catch (_) {
      return; // 상태를 못 읽으면 판정을 미룬다 — 언마운트 cleanup 이 맡는다
    }
    if (dirty && !usable) {
      setToast({ type: 'error', text: `${label}을 완전히 비울 수는 없어 저장본으로 되돌렸습니다.` });
    }
    if (!usable) setDirty(false);
    pendingRef.current = usable
      ? { state: usable, dirty: true, key, flushed: true }
      : { dirty: false, key, flushed: true };
  };

  // ── 라벨 초안 ── 서버 목록에 대어 늘 다시 센다(labelEdit 머리주석).
  // 목록은 상세가 실으면 그것(279bbed), 아니면 따로 읽은 것(옛 서버).
  const labelReady = detailHasLabels
    || (labelServer.status === 'ready' && data != null && String(labelServer.forId) === String(data.problem_id));
  let labelItems = [];
  if (detailHasLabels) labelItems = data.labels;
  else if (labelReady) labelItems = labelServer.items;
  const labelPlanNow = labelReady ? labelPlan(labelItems, labelDraft) : null;
  // 목록을 다시 읽는 한 박자 동안에도 초안이 남아 있으면 미저장이다 — 그 틈에 이탈 가드가 풀리지 않게.
  // 단 **저장이 끝나 새 목록을 기다리는 동안**(clearLabelDraftOnLoadRef)은 세지 않는다 — 서버에는 이미 반영됐다.
  // 이것을 미저장으로 세면 저장 직후 부모가 올리는 refreshKey 가 「편집 중」 갈래로 빠져, 방금 낸 「저장되었습니다」를
  // 「다른 곳에서 변경이 있었지만…」으로 덮는다(실측 2026-09-13 — ProblemViewTab.labelEdit.test 가 못박는다).
  const labelSettling = clearLabelDraftOnLoadRef.current;
  const labelDirty = !labelSettling && ((labelReady
    ? !isLabelPlanEmpty(labelPlanNow)
    : (labelDraft.added.length + labelDraft.removed.length) > 0)
    || labelInput.trim() !== '');

  // ── 개념·난이도 ── 옛 서버면 칸을 잠그므로 초안도 세지 않는다(canEditMeta — 머리주석)
  const canEditMeta = detailHasLabels;
  const conceptDirty = canEditMeta && data != null && !sameIdSet(conceptIds, data.concept_ids);
  const difficultyDirty = canEditMeta && data != null
    && difficulty !== (data.difficulty != null ? String(data.difficulty) : '');
  // 이름 — 상세의 concept_names 가 먼저(서버가 준 이름), 없으면 사전, 그것도 없으면 번호
  const nameOfConcept = (id) => (data?.concept_names || []).find((c) => String(c.concept_id) === String(id))?.name
    || dictById.get(String(id))?.concept_name || `#${id}`;
  // 폐기된 개념 — 서버가 400 으로 거절하지만 어느 것인지 말하지 않는다(회신 §3 4-2). 여기서 이름을 붙여 말한다.
  const outdatedPicked = conceptIds.filter((id) => dictById.get(String(id))?.is_outdated);
  const outdatedText = (ids) => ids.map((id) => {
    const rep = (dictById.get(String(id))?.replaced_to || []).map(nameOfConcept);
    return `「${nameOfConcept(id)}」${rep.length > 0 ? `(대체: ${rep.map((n) => `「${n}」`).join('·')})` : ''}`;
  }).join(' · ');

  // ── dirty ──
  const answerDirty = data != null && normAnswer(answerText) !== normAnswer(data.answer);
  const dirty = answerDirty || bodyDirty || solDirty || labelDirty || conceptDirty || difficultyDirty;

  // 부모의 이탈 가드에 알린다. 값이 바뀔 때 + 언마운트에서 false.
  const onDirtyRef = useRef(onDirtyChange);
  onDirtyRef.current = onDirtyChange;
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  // closeFigure 는 useCallback([load]) 안에서 이것을 부른다 — prop 을 직접 쓰면 deps 에 넣어야 하고,
  // 그러면 부모가 인라인 함수를 넘길 때마다 콜백이 새로 만들어져 FigureModal 이 다시 그려진다.
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;
  useEffect(() => { onDirtyRef.current?.(dirty); }, [dirty]);
  useEffect(() => () => onDirtyRef.current?.(false), []);

  // 저장 판정용 — 배지 state 가 아니라 라이브/스냅샷을 직접 읽는다(최신값).
  const hasUnsaved = () => {
    if (loading || !data) return false;
    if (normAnswer(answerText) !== normAnswer(data.answer)) return true;
    if (labelDirty || conceptDirty || difficultyDirty) return true;
    if (readLexEdit(bodyEditorRef, bodyLexPendingRef, lexKey('b')) != null) return true;
    if (readLexEdit(solutionEditorRef, solutionLexPendingRef, lexKey('s')) != null) return true;
    return false;
  };
  const hasUnsavedRef = useRef(hasUnsaved);
  hasUnsavedRef.current = hasUnsaved;

  // 그림 대조에 쓸 **저장본**. handleFigureAction 이 의존성 없는 useCallback 이라(편집기가 매
  // 렌더마다 다시 그려지지 않게) state 를 직접 읽을 수 없어 ref 로 나른다.
  const dataRef = useRef(null);
  dataRef.current = data;
  // 내가 쥐고 있는 낙관락 값 — 효과의 의존성에 data 를 넣지 않으려고 ref 로 흘린다.
  const knownUpdatedAtRef = useRef(null);
  knownUpdatedAtRef.current = data?.updated_at ?? null;

  // 서버에서 이 문제가 바뀌었다는 신호(refreshKey)가 오면 다시 읽는다 — 단, 편집 중이면 덮지
  // 않는다. 고치던 값을 서버 값으로 되돌리는 것이 더 나쁘다.
  //
  // ⚠ 편집 중일 때의 안내가 2026-08-26 계약 §3 으로 **거짓이 됐다.** 예전 문구는 "저장한 뒤 다시
  //   읽으면 최신 값이 보입니다"였는데, 이제 분석 승인·본문 되돌리기가 `Problems.updated_at` 을
  //   전진시켜 그 저장이 **409 stale 로 막힌다**. 감사 C1 이 지목한 것이 바로 이 흐름이다
  //   ("FE 는 승인 직후 편집 중이면 재로딩 대신 저장을 안내한다").
  //   반대로 신호만 보고 무조건 잠그면 안 된다 — refreshKey 는 변형 승인처럼 **이 문제를
  //   건드리지 않는** 일에도 올라오고(새 문제가 생길 뿐이다), 그때는 멀쩡한 저장까지 막힌다.
  //   전진했는지는 서버에만 있으므로, 상세를 한 번 더 읽어 **updated_at 만 대조한다**.
  //   화면은 덮지 않는다(setData 하지 않는다) — 편집분을 지키는 것이 이 분기의 목적이다.
  const prevRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (prevRefreshKeyRef.current === refreshKey) return undefined;
    prevRefreshKeyRef.current = refreshKey;
    if (problemId == null) return undefined;
    if (!hasUnsavedRef.current()) {
      load();
      return undefined;
    }
    let alive = true;
    (async () => {
      const known = knownUpdatedAtRef.current;
      try {
        const fresh = await fetchProblem(problemId, getAccessToken);
        if (!alive || String(problemId) !== String(targetRef.current)) return;
        const moved = known && fresh?.updated_at && String(fresh.updated_at) !== String(known);
        if (moved) {
          setStale(true);
          setToast({
            type: 'error',
            text: '이 문제가 서버에서 바뀌었습니다(분석 승인·본문 되돌리기 등) — 지금 저장하면 그 수정을 덮게 되어 막았습니다. 내 편집은 그대로 두었습니다.',
          });
        } else {
          setToast({ type: 'info', text: '다른 곳에서 변경이 있었지만 이 문제의 본문·정답·풀이·개념·난이도는 그대로입니다 — 그대로 저장할 수 있습니다.' });
        }
      } catch (_) {
        // 대조에 실패하면 확인하지 못한 것을 단정하지 않는다. 잠그지도 않는다 —
        // 저장은 어차피 서버가 409 로 막아 준다(그것이 §3 이 바꾼 바로 그 부분이다).
        if (alive) setToast({ type: 'info', text: '서버에서 이 문제가 바뀌었을 수 있습니다 — 저장이 막히면 [새로 읽기]로 최신을 받아 오세요.' });
      }
    })();
    return () => { alive = false; };
  }, [refreshKey, problemId, load, getAccessToken]);

  // 미리보기는 자기 포털을 쓰고 ESC 를 듣지 않는다 — 여기서 얹는다.
  // (편집기가 자기 영역의 ESC 를 삼키지만, 미리보기가 떠 있는 동안 커서는 포털 안이라 닿는다.)
  useEffect(() => {
    if (!previewOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPreviewOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [previewOpen]);

  // ── 저장: 바뀐 것만 PATCH 한 번 ──
  const handleSave = async () => {
    if (saving || !data) return false;
    // 409 를 만난 뒤에는 막는다 — 낙관락 값이 그대로라 다시 보내 봐야 또 막히고,
    // 그 사이 남의 저장을 덮을 길만 늘어난다.
    if (stale) {
      setToast({ type: 'error', text: '내가 읽은 뒤 이 문제가 바뀌었습니다 — [새로 읽기]로 최신을 받은 뒤 다시 시도하세요.' });
      return false;
    }
    const patch = {};

    const curAnswer = normAnswer(answerText);
    // null 은 여기서 "정답 비우기"다 — 변형·생성 검수의 null(=수정 철회)과 뜻이 정반대다.
    if (curAnswer !== normAnswer(data.answer)) patch.answer = curAnswer;

    const bodyLex = readLexEdit(bodyEditorRef, bodyLexPendingRef, lexKey('b'));
    if (bodyLex != null) patch.body_lexical = bodyLex;
    const solLex = readLexEdit(solutionEditorRef, solutionLexPendingRef, lexKey('s'));
    if (solLex != null) patch.solution_lexical = solLex;

    // 개념·난이도 — 바뀐 것만 같은 PATCH 에 싣는다(회신 BE-20260913 §2). 비우기는 서버가 400 으로 거절하므로
    // 보내기 전에 막고, 폐기 개념은 서버 문구가 어느 것인지 말하지 않아 여기서 이름을 붙여 막는다(오너 결정 C).
    if (conceptDirty) {
      if (conceptIds.length === 0) {
        setToast({ type: 'error', text: '개념은 비울 수 없습니다 — 하나 이상 남기고 저장하세요.' });
        return false;
      }
      if (outdatedPicked.length > 0) {
        setToast({ type: 'error', text: `폐기된 개념이 있어 저장하지 않았습니다 — ${outdatedText(outdatedPicked)}. 떼고 다시 저장하세요.` });
        return false;
      }
      patch.concept_ids = conceptIds;
    }
    if (difficultyDirty && difficulty !== '') patch.difficulty = Number(difficulty);

    /**
     * ── 본문이 바뀐 field 의 그림을 **서버 목록과 맞춘다**(삭제 → 본문 PATCH) ──
     *
     * 그림 삭제는 DELETE API 여야 한다(회신 §2 Q8) — 본문 PATCH 로 image 노드가 사라지면 서버는
     * 그 사실을 몰라 지운 그림의 이력이 남고 뒤 그림들의 index 가 어긋난다. 어떤 방법으로 지웠든
     * (Backspace·범위 선택·드래그·[지우기]) 저장할 때 삭제 API 로 돌린다(purgeRemovedFigures).
     *
     * **자는 「저장본 대 새 문서」가 아니라 「서버 목록 대 새 문서」다**(2026-09-04 실측 정정, 검수
     * 모달과 같은 판단). 저장본과의 개수 비교는 한 장을 지우고 한 장을 넣은 저장을 놓쳤고, 한 번
     * 놓친 그림은 다음 저장도 영영 잡지 못했다. 본문이 바뀔 때마다 목록을 한 번 읽어 맞춘다.
     * 등록은 없다 — 은행은 본문 저장이 곧 그림 목록 반영이다(계약 §10).
     *
     * 문서를 읽지 못한 field(null)는 건드리지 않는다 — 모르는 것으로 지우지 않는다.
     */
    const lexPlans = [
      patch.body_lexical != null ? { field: 'body', nodes: collectFigureNodes(patch.body_lexical) } : null,
      patch.solution_lexical != null ? { field: 'solution', nodes: collectFigureNodes(patch.solution_lexical) } : null,
    ].filter((x) => x && Array.isArray(x.nodes));

    // ── 라벨 ── 입력칸에 남은 글자도 넣는다(Enter 를 잊고 누른 경우 — 검수 창 effectiveLabels 와 같은 관용).
    let draftForSave = labelDraft;
    if (labelInput.trim() !== '') {
      if (!labelReady) {
        setToast({ type: 'error', text: '라벨 목록을 읽는 중입니다 — 잠시 뒤 다시 저장하세요.' });
        return false;
      }
      const r = addLabelTokens(labelItems, labelDraft, splitLabels(labelInput));
      if (r.rejected.length > 0) {
        // 조용히 빼고 저장하지 않는다 — 친 글자가 사라지면 사람은 붙은 줄 안다
        setToast({ type: 'error', text: describeRejectedLabels(r.rejected) });
        return false;
      }
      draftForSave = r.draft;
      setLabelDraft(r.draft);
      setLabelInput('');
    }
    if (!labelReady && (draftForSave.added.length + draftForSave.removed.length) > 0) {
      setToast({ type: 'error', text: '라벨 목록을 읽는 중입니다 — 잠시 뒤 다시 저장하세요.' });
      return false;
    }
    const lplan = labelReady ? labelPlan(labelItems, draftForSave) : null;
    const hasLabelWork = !isLabelPlanEmpty(lplan);
    const hasPatch = Object.keys(patch).length > 0;

    if (!hasPatch && !hasLabelWork) {
      setToast({ type: 'info', text: '변경 사항이 없습니다.' });
      return true;
    }

    setSaving(true);
    setToast(null);
    const target = data.problem_id;
    const sameTarget = () => String(target) === String(targetRef.current);
    // 이번 저장이 서버에서 지운 그림 수 — 되돌릴 수 없는 쓰기라 저장 문구가 함께 말한다.
    let purgedCount = 0;
    try {
      // 낙관락 — 직전 상세의 updated_at 을 그대로 되돌려 보낸다. 없이도 받지만, 안 보내면
      // 나중 저장이 앞 저장을 말없이 덮는다. **삭제가 이 값을 전진시키므로** 그 뒤에 싣는다.
      let token = data.updated_at;
      if (lexPlans.length > 0) {
        const r = await purgeRemovedFigures('bank', target, lexPlans, { expectedUpdatedAt: token }, getAccessToken);
        if (r === AUTH_REDIRECT) return false;
        if (!r.ok) {
          // 못 가르는 경우다(대조할 src 없는 그림이 일부만 사라짐 등). 본문도 저장하지 않는다 —
          // 저장하면 서버가 모르는 삭제가 되어 이 관문이 애초에 막으려던 일이 일어난다.
          setToast({ type: 'error', text: describePurgeFailure(r.reason, r.field) });
          return false;
        }
        token = r.updatedAt;
        purgedCount = Object.values(r.deleted || {}).reduce((s, a) => s + (Array.isArray(a) ? a.length : 0), 0);
      }
      // 순서: 문제 PATCH → 라벨. 라벨 쓰기가 updated_at 을 올리므로 PATCH 가 먼저 가야 낙관락이 제 값으로 선다.
      if (hasPatch) {
        if (token) patch.expected_updated_at = token;
        const d = await updateProblem(target, patch, getAccessToken);
        if (d === AUTH_REDIRECT) return false; // 401 리다이렉트 중 — 성공이라고 말하지 않는다
        // 응답이 도착했을 때 다른 문제로 옮겨 갔으면 화면을 덮지 않는다.
        if (sameTarget()) {
          seqRef.current += 1; // in-flight load 의 구응답이 저장 결과를 되덮지 않게
          // 응답이 갱신된 상세다(§B-1) — 그것으로 화면과 기준선을 함께 갱신한다.
          // 새 updated_at 이 다음 편집의 낙관락 값이 된다.
          setData(d);
          // 렌더를 기다리지 않고 바로 갱신한다 — 저장 직후 이어지는 그림 작업(handleFigureAction 의
          // 자동 저장)이 이 값을 읽는데, setState 뒤의 await 이음새에서는 아직 옛 상세가 들어 있다.
          dataRef.current = d;
          onLoadedRef.current?.(d);
          seed(d);
        }
      }

      if (hasLabelWork) {
        const rawBefore = dataRef.current?.labels_raw ?? null;
        let labelErr = null;
        let applied = null;
        try {
          applied = await applyLabelPlan(target, lplan, getAccessToken);
        } catch (le) {
          labelErr = le;
        }
        if (applied === AUTH_REDIRECT) return false;
        // 라벨 쓰기는 문제의 updated_at 을 올린다 — 성공이든 중간 실패든 상세를 다시 읽어 낙관락 값과 화면을 맞춘다.
        // 새 라벨 목록은 그 상세에 실려 온다(279bbed). 옛 서버면 labels_raw 가 바뀐 것을 보고 effect 가 목록을 다시 읽고,
        // 성공이면 그 목록과 같은 박자에 초안을 비운다(clearLabelDraftOnLoadRef — 그 사이 미저장으로 세지 않는다).
        // 중간에 멈췄으면 초안을 그대로 둔다 — 다시 읽은 목록에 대어 남은 것만 다시 센다.
        if (!labelErr) clearLabelDraftOnLoadRef.current = true;
        let fresh = null;
        try {
          fresh = await fetchProblem(target, getAccessToken);
        } catch (_) { /* 아래에서 말한다 */ }
        if (sameTarget()) {
          if (fresh) {
            seqRef.current += 1;
            setData(fresh);
            dataRef.current = fresh;
            onLoadedRef.current?.(fresh);
            seed(fresh);
            if (Array.isArray(fresh.labels)) {
              // 상세가 새 라벨 목록을 실어 왔다 — 그 목록과 같은 박자에 초안을 비운다(따로 읽을 것이 없다)
              clearLabelDraftOnLoadRef.current = false;
              if (!labelErr) setLabelDraft(EMPTY_LABEL_DRAFT);
            } else if (String(fresh.labels_raw ?? '') === String(rawBefore ?? '')) {
              // 옛 서버 — 문자열이 그대로면 effect 가 돌지 않는다(사전 연결만 바뀐 경우). 목록을 직접 다시 읽는다
              loadLabels();
            }
          }
          if (labelErr) {
            setToast({ type: 'error', text: `${hasPatch ? '라벨 밖의 수정은 저장했습니다. ' : ''}${describeLabelError(labelErr)}` });
          } else if (!fresh) {
            setToast({ type: 'error', text: '라벨은 저장했지만 문제를 다시 읽지 못했습니다 — 이어서 고치기 전에 이 문제를 다시 여세요.' });
          }
        }
        if (labelErr || !fresh) {
          onChanged?.();
          return false;
        }
      }

      if (sameTarget()) {
        setToast({
          type: 'info',
          text: purgedCount > 0 ? `저장되었습니다 — 그림 ${purgedCount}장 삭제.` : '저장되었습니다.',
        });
      }
      onChanged?.();
      return true;
    } catch (e) {
      /**
       * **삭제가 중간에 멈췄다** — 이미 지운 것은 서버에 반영돼 있다. 그 사실을 반드시 먼저
       * 말한다: 「저장에 실패했습니다」로만 끝내면 사람은 아무 일도 없었다고 믿고 같은 것을
       * 다시 지운다(그러면 그때는 다른 그림이 지워진다 — 순번이 이미 당겨졌으므로).
       */
      if (e?.partial?.deleted?.length) {
        setToast({
          type: 'error',
          text: describePartialPurge(e.partial.deleted.length, e.partial.field, describeFigureError(e).text),
        });
        return false;
      }
      // 낙관락은 **`data.reason`** 으로 가른다(계약 §3 — 이전에는 409 에 data 가 null 이라
      // 상태코드밖에 볼 것이 없었다). message 문자열로 갈라 두면 백엔드가 문구를 다듬는 날
      // 조용히 어긋나므로, 여기서는 코드로만 본다. reason 없는 409 는 옛 배포분과 섞여 돌 때의
      // 안전망으로 남긴다 — 이 엔드포인트의 409 는 낙관락 하나뿐이다.
      if (e?.data?.reason === 'stale' || e?.httpStatus === 409) {
        // 편집을 덮어쓰지 않는다 — 여기서 자동으로 다시 읽으면 그 순간 자기 편집이 사라진다.
        setStale(true);
        setToast({
          type: 'error',
          // 사람이 먼저 저장한 것만이 아니다 — 분석 승인·본문 되돌리기도 이제 낙관락을 돌린다(§3).
          text: '내가 읽은 뒤 이 문제가 바뀌었습니다(다른 사람의 저장 · 분석 승인 · 본문 되돌리기) — 내 편집은 그대로 두었습니다.',
        });
        return false;
      }
      setToast({ type: 'error', text: e.message || '저장에 실패했습니다.' });
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * **그림을 서버에 쓰기 전에 본문을 먼저 저장한다** — 그림 창·AI 생성이 이것을 지난다.
   * 그 둘은 서버 본문을 바로 바꾸고 화면이 상세를 다시 읽는데, 그 순간 저장하지 않은 편집이 덮인다.
   * 종전에는 「먼저 [저장]하세요」로 막았다(검수 모달의 ensureSaved 와 같은 정정, 2026-09-04).
   * 저장이 곧 지운 그림의 삭제이기도 하므로, 이것을 지나면 문서와 서버 목록이 같은 그림을 센다.
   */
  const ensureSaved = async () => {
    if (!hasUnsaved()) return true;
    return handleSave();
  };
  ensureSavedRef.current = ensureSaved;

  // 편집을 버리고 서버 값으로 되세운다. 스냅샷은 키가 어긋나 자동 폐기되고, seed 가 마저 비운다.
  const discardEdits = () => {
    bodyLexPendingRef.current = null;
    solutionLexPendingRef.current = null;
    setBodyDirty(false);
    setSolDirty(false);
    setSolutionDraft(false);
    setAnswerText(data?.answer ?? '');
    setAnswerEditing(false);
    setConceptIds(Array.isArray(data?.concept_ids) ? data.concept_ids : []);
    setDifficulty(data?.difficulty != null ? String(data.difficulty) : '');
    setLabelDraft(EMPTY_LABEL_DRAFT);
    setLabelInput('');
    setLexResetSeq((n) => n + 1);
  };

  // 그림 생성도 서버 쓰기라 함께 잠근다 — 그 사이 [저장]이 나가면 낙관락이 서로를 밀어낸다
  const busy = saving || figGenBusy;

  const switchMode = (next) => {
    if (next === mode) return;
    // 편집기가 살아 있는 지금만 믿을 수 있다 — 언마운트 뒤엔 빈 문서로 읽힌다.
    flushLexBeforeSwitch('본문', bodyEditorRef, bodyLexPendingRef, lexKey('b'), setBodyDirty);
    flushLexBeforeSwitch('풀이', solutionEditorRef, solutionLexPendingRef, lexKey('s'), setSolDirty);
    setMode(next);
  };

  /**
   * 두 번 누르기로 편집에 들어갈 때 **눈이 보던 자리를 붙들어 둔다**(오너 보고 2026-09-01
   * 「스크롤이 변한다」).
   *
   * 왜 밀리는가 — 모드는 세 칸을 **함께** 바꾼다. 편집이 되면 본문 상자 안에 도구줄이 생기고,
   * 정답은 한 줄짜리 보기 칸에서 [①~⑤] 줄 + 입력칸 + 두 줄 안내로 자란다. 그 위에서 늘어난
   * 높이만큼 아래 것이 내려가므로, 풀이를 두 번 누른 사람은 방금 누른 글이 화면 밖으로 밀린다.
   * scrollTop 은 그대로인데 내용이 움직인 것이라, 눈에는 「스크롤이 튀었다」로 보인다.
   *
   * 그래서 누른 칸의 높이를 재 두었다가, 새 화면이 그려진 **직후**(그리기 전에) 같은 자리를
   * 다시 재서 차이만큼 스크롤을 민다. useEffect 가 아니라 useLayoutEffect 인 이유가 이것이다 —
   * 브라우저가 한 번 그린 뒤에 옮기면 그 한 프레임이 그대로 깜빡임으로 보인다.
   *
   * [편집] 버튼에는 걸지 않는다. 그 버튼은 본문 머리에 있어 누르는 순간 이미 맨 위이고,
   * 밀리는 것은 그 아래뿐이라 붙들 자리가 없다.
   */
  const scrollAnchorRef = useRef(null);
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    scrollAnchorRef.current = null;
    if (!anchor || !anchor.el.isConnected) return;
    const delta = anchor.el.getBoundingClientRect().top - anchor.top;
    if (Math.abs(delta) < 1) return; // 1px 미만은 사람 눈에 없는 것이고, 밀면 오히려 흔들린다
    const box = scrollableAncestor(anchor.el);
    if (box) box.scrollTop += delta;
    else window.scrollBy(0, delta);
  }, [mode]);

  // ── 그림 편집 (BE-20260827) ──
  // 편집기 안 그림을 선택하면 뜨는 버튼이 여기로 올라온다.
  // 계약: docs/exam-builder-v2/BE-20260827-figure-editing-apis.md (§8 이 2026-08-29 갱신분)
  // 회신: docs/exam-builder-v2/BE-20260829-reply-fe-0828.md §2 — Q1·Q2·Q3·Q5·Q6 확정
  //
  // 버튼 세트는 그림 종류로 갈린다(오너 결정 2026-08-28) —
  //   SVG   → [편집]                        : 명세를 고친다
  //   픽셀  → [재생성] [교체] [삭제](옛 이름)  : 각각 reconvert · 이력 restore · DELETE API
  //
  // 여기서 잠그는 것이 이 기능의 주 방벽이다. 계약의 index 는 **저장된 본문의 문서 순서**인데
  // 화면이 들고 있는 것은 미저장 편집분일 수 있다. 그림을 하나 지우거나 넣은 채로 쓰기를 보내면
  // 서버는 **다른 그림에 성공적으로** PATCH 한다 — 200 이 오므로 화면은 성공이라 말하고,
  // 사람은 엉뚱한 그림이 바뀐 것을 모른다. 그래서 미저장분이 있으면 아예 보내지 않는다.
  // 판정은 배지 state(dirty) 가 아니라 hasUnsaved() 로 한다 — 그쪽이 라이브 편집기를 직접 읽어
  // 최신이고, 정답칸처럼 편집기 밖의 미저장 입력까지 함께 본다.
  //
  // **저장본에 있는 그림의 삭제도 이 잠금을 지난다**(2026-08-29 회신 §2 Q8). 종전에는 예외였다 —
  // 편집기 안에서 노드를 빼고 [저장]이 본문 PATCH 로 내보내는 일이라 「서버에 나가는 것이 없어
  // 잠금 대상이 아니다」라고 적어 두었다. 회신이 그 길을 금지했다: 서버가 노드가 사라진 것을 몰라
  // 지운 그림의 이력이 남고 뒤 그림들의 index 가 밀린다. 삭제는 이제
  // `DELETE …/figures/{field}/{index}` 로 나가며 **순번으로 지정되고 되돌릴 수 없다**.
  //
  // 다만 편집기 키(Delete·Backspace·Cmd+X)를 잡는 것만으로는 **범위 선택**이 남는다 — 그림을
  // 문단째 긁어 지우거나 그 위에 타이핑하면 RangeSelection 이라 그림 노드의 명령에 걸리지 않는다.
  // 그 그물은 저장 직전에 있다(handleSave 의 「그림이 줄어든 저장」 검사) — 명령을 더 잡는 것보다
  // 확실하고, 잘라내기·드래그·붙여넣기 덮어쓰기까지 한 번에 덮는다.
  //
  // 반환값은 **「내가 맡았는가」**다(FigureEditContext 의 계약). `false` 를 돌려주면 편집기가
  // 종전대로 노드를 지운다.
  //
  // ── 2026-09-04: 지우기는 편집기의 일, 서버 삭제는 [저장]의 일 ──
  // 종전에는 서버가 아는 그림의 [지우기]·Backspace 가 삭제 창을 열어 **즉시** DELETE 를 보냈고,
  // 미저장 편집이 있으면 「먼저 저장하라」로 막았다. 이제 어느 그림이든 편집기가 노드를 빼고,
  // [저장]이 사라진 그림을 삭제 API 로 돌린다(purgeRemovedFigures). 저장 전에는 실행 취소로 되돌린다.
  //
  // ── 나머지 셋(편집·AI 요청·이미지 교체)은 **먼저 저장하고** 연다(ensureSaved) ──
  // 그림 창은 서버에 바로 쓰고 닫힐 때 상세를 다시 읽는다 — 저장하지 않은 편집이 그때 덮인다.
  // 종전의 「먼저 [저장]하거나 [편집 취소] 후에 열 수 있습니다」 왕복을 없앤다(검수 모달과 같은 정정).
  const handleFigureAction = useCallback(({ action, field, index, src }) => {
    if (action === 'delete') return false;
    if (index < 0) {
      // 노드를 문서에서 못 찾았다 — 여기까지 오면 안 되는 경로다(버튼은 그 노드 안에 있다).
      setToast({ type: 'error', text: '그 그림을 본문에서 찾지 못했습니다 — 창을 닫았다 다시 열어 주세요.' });
      return true;
    }
    const pid = dataRef.current?.problem_id;
    (async () => {
      const ok = await ensureSavedRef.current?.();
      if (!ok) return; // 사유는 handleSave 가 말했다
      const d = dataRef.current;
      if (pid == null || String(d?.problem_id) !== String(pid)) return; // 저장하는 사이 문제를 옮겼다
      // 대조에 쓸 화면 쪽 그림 줄 — **저장본**(방금 저장한 상세의 lexical)을 훑는다. 은행 `item.src` 는
      // 원문 그대로라(회신 Q6) 바이트까지 맞춰 볼 수 있다.
      const doc = field === 'solution' ? d?.solution_lexical : d?.body_lexical;
      const nodes = collectFigureNodes(doc);
      // 창이 목록을 받아 이 index·이 줄과 대조한 뒤에야 쓰기를 보낸다(FigureModal 의 순번 대조).
      setFigureTarget({ action, field, index, src, nodes });
    })();
    return true;
  }, []);

  // 그림 창을 닫으면 **무조건** 상세를 다시 읽는다.
  //
  // changed 로 걸지 않는 이유: 계약 §6 이 「그림 목록·이력 첫 조회는 쓰기(seed)를 동반한다」고 했다.
  // 그 seed 가 낙관락 토큰을 전진시키지는 **않는다** — 회신 §2 Q4 가 실측으로 확인했다(2026-08-29).
  // 그래도 조건 없이 읽는 이유는 남는다: 이 창은 그림을 실제로 고쳤을 수 있고(PATCH·재요청·되돌리기가
  // 본문과 updated_at 을 함께 움직인다), 창이 무엇을 했는지 바깥이 신뢰해 판단하는 것보다
  // 한 번 더 읽는 값이 싸다.
  // 삭제로 닫힌 경우에만 result 가 실려 온다 — 창이 사라진 뒤라 그 사실을 말할 자리가 여기뿐이다.
  // `remaining` 은 쓰지 않는다: 그 field 의 남은 수인지 문제 전체인지 계약에 없다(→ 백엔드 질의).
  const closeFigure = useCallback((changed, result) => {
    setFigureTarget(null);
    if (result?.deleted) {
      setToast({ type: 'info', text: `${result.field === 'solution' ? '풀이' : '본문'}에서 그림을 지웠습니다.` });
    }
    load();
    // 그림 저장·재요청·삭제는 **본문 이력에 판을 하나 쌓는다**(계약 §4-2). 그 사실을 밖에도
    // 알려야 [수정 이력] 탭이 다시 읽는다 — 그 패널은 hidden 으로만 감춰져 스스로는 모른다.
    // 위 load() 와 겹쳐 상세를 한 번 더 읽지만(refreshKey 효과가 편집 중이 아니면 load 한다),
    // 이력이 조용히 낡는 것보다 GET 한 번이 싸다.
    onChangedRef.current?.();
  }, [load]);

  /**
   * ── AI 생성 (계약 §9-2, 2026-08-30) ──
   *
   * 본문·풀이의 글을 AI 가 읽고 그림 하나를 지어 **그 field 맨 끝에 붙인다.** 있던 그림을
   * 바꾸는 것이 아니라 더하는 것이라, 대체하려면 있던 그림을 지우고 부른다.
   *
   * **미저장 편집분이 있으면 먼저 저장한다**(ensureSaved). 이 호출은 서버 본문에 image 노드를 붙이고
   * 본문 이력에 판을 쌓으며 `Problems.updated_at` 을 전진시킨다(계약 §9-2). 성공한 뒤 화면이 최신을
   * 다시 읽을 때 고치던 것이 덮이지 않게 저장을 앞세운다 — 그림 편집 창(handleFigureAction)과 같은 규칙.
   */
  const openFigGen = useCallback(async (field) => {
    const ok = await ensureSavedRef.current?.();
    if (!ok) return; // 사유는 handleSave 가 말했다
    setToast(null);
    setFigGenField(field);
    /**
     * 이 자리 그림들의 원본 카드(2026-09-06 A 안) — 서버 목록을 읽어 짓는다. 창은 먼저 열고 카드는 도착하면
     * 늘어난다. 읽기가 실패해도 창은 쓸 수 있다 — 본문 글·올린 이미지 갈래는 목록과 무관하다.
     */
    const seq = (figGenSeqRef.current += 1);
    const pid = dataRef.current?.problem_id;
    setFigGenOrigins({ loading: pid != null, items: [], token: null });
    if (pid == null) return;
    try {
      const d = await fetchFigures('bank', pid, getAccessToken);
      if (d === AUTH_REDIRECT || seq !== figGenSeqRef.current) return;
      setFigGenOrigins({ loading: false, items: listFigureOrigins(d?.items, 'bank', field), token: d?.updated_at || null });
    } catch {
      if (seq === figGenSeqRef.current) setFigGenOrigins({ loading: false, items: [], token: null });
    }
  }, [getAccessToken]);

  /**
   * 편집기가 **이 문제의 것으로 살아 있을 때까지** 잠깐 기다린다. 보기 모드에서 [이미지 추가]를 누르면
   * 편집 모드로 넘어가며 편집기가 새로 뜨는데, 그 마운트는 다음 렌더 뒤다(stateRef 는 마운트 effect 가 채운다).
   */
  const waitForEditor = async (field, tries = 40) => {
    const ref = field === 'solution' ? solutionEditorRef : bodyEditorRef;
    const key = lexKey(field === 'solution' ? 's' : 'b');
    for (let i = 0; i < tries; i += 1) {
      const live = ref.current;
      if (live && live.key === key && typeof live.insertImage === 'function') return live;
      // eslint-disable-next-line no-await-in-loop -- 마운트를 기다리는 폴링이다
      await new Promise((r) => { setTimeout(r, 50); });
    }
    return null;
  };

  /**
   * 이미지를 편집기 끝에 넣는다 — 은행은 **저장이 곧 등록**이라(계약 §10) 서버 호출이 없다.
   * 보기 모드면 편집 모드로 옮기고 편집기를 기다린다(두 번 누르기와 같은 길 — switchMode 가 편집분을 지킨다).
   */
  const insertImageNow = async (field, src) => {
    if (mode !== 'edit') switchMode('edit');
    const live = await waitForEditor(field);
    return !!(live && live.insertImage({ src, altText: '' }));
  };

  /** [이미지 추가] → [이미지 파일 추가] — 검수 모달과 같은 규격 검사(형식·2MB)를 지나 편집기에 넣는다. */
  const openImagePicker = (field) => {
    imageInputFieldRef.current = field;
    if (imageInputRef.current) imageInputRef.current.value = '';
    imageInputRef.current?.click();
  };
  /** 편집기에 넣고 결과를 말한다 — 검수 모달의 insertImageNow 와 같은 문구. */
  const insertAndTell = async (field, src) => {
    const where = field === 'solution' ? '풀이' : '본문';
    const ok = await insertImageNow(field, src);
    setToast(ok
      ? { type: 'info', text: `${where}에 이미지를 넣었습니다 — [저장]하면 그림으로 반영됩니다.` }
      : { type: 'error', text: `${where} 편집기에 넣지 못했습니다 — 편집 모드에서 다시 시도해 주세요.` });
    return ok;
  };

  /**
   * **들어오는 이미지의 한 입구** — 붙여넣기·드롭·툴바 삽입(PasteFigurePlugin)과 [이미지 파일 추가]가 다 여기로
   * 온다(검수 모달의 takeIncomingImage 와 같은 규칙).
   *  ① 서버가 받지 않는 이미지(형식·2MB)는 여기서 막는다.
   *  ② 그 자리에 **바꿀 그림이 없으면 묻지 않고 넣는다** — 저장이 곧 반영이다(계약 §10 G2).
   *  ③ 바꿀 그림이 있을 때만 창을 연다(OcrPasteImageModal target='bank') — 새 그림으로 넣기 / N번째 바꾸기.
   */
  const takeIncomingImage = ({ field: f, src, altText = '', precedingSrcs = null }) => {
    const field = f === 'solution' ? 'solution' : 'body';
    const check = parseImageDataUri(src);
    if (!check.ok) { setToast({ type: 'error', text: describeImageReject(check) }); return; }
    const d = dataRef.current;
    const saved = collectFigureNodes(field === 'solution' ? d?.solution_lexical : d?.body_lexical);
    if (!saved || saved.length === 0) { insertAndTell(field, src); return; }
    setPasteTarget({ field, src, altText, precedingSrcs });
  };
  takeIncomingRef.current = takeIncomingImage;
  // 편집기가 이미지를 받으려는 순간(PasteFigurePlugin) — 판정은 takeIncomingImage 가 한다.
  const handlePasteImage = useCallback((incoming) => { takeIncomingRef.current?.(incoming); }, []);

  const takeImageFile = (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { setToast({ type: 'error', text: '이미지 파일만 넣을 수 있습니다.' }); return; }
    const reader = new FileReader();
    reader.onload = () => takeIncomingImage({
      field: imageInputFieldRef.current, src: String(reader.result || ''), altText: '', precedingSrcs: null,
    });
    reader.onerror = () => setToast({ type: 'error', text: '이미지를 읽지 못했습니다.' });
    reader.readAsDataURL(file);
  };

  /**
   * 생성 창(FigureReconvertModal kind='generate')이 고른 것으로 만든다 — 검수 모달과 같은 창, 같은 갈래.
   *  · source 'text'   본문 글로 짓는다 — `POST …/figures/generate`
   *  · source 'upload' **올린 이미지로 새 그림을 만든다.** 은행에는 등록 API 가 없어(계약 §10 「편집기 저장이
   *    곧 반영」) 편집기에 넣고 → 저장하고 → 서버 목록에서 그 그림을 바이트로 찾아(회신 Q6) → 재요청한다.
   *    재요청이 거절되거나 실패해도 이미지는 그림으로 남는다 — 손실은 없고, 그 사실을 말한다.
   *  · source 'figure' **이 자리 그림의 원본으로 그 그림을 그 자리에서 다시 그린다**(2026-09-06 오너 결정 A 안).
   *    보관 원본이면 `use_original:true`, 지면 크롭뿐이면 그 크롭을 `image` 로, 픽셀은 비운다(figureOrigins 가 정한다).
   */
  const runFigGen = async ({ source, image, mode: genMode, userPrompt, figure } = {}) => {
    const field = figGenField;
    const pid = dataRef.current?.problem_id;
    if (!field || pid == null) return;
    setFigGenBusy(true);
    setToast(null);
    try {
      if (source === 'figure' && figure) {
        const where = figure.field === 'solution' ? '풀이' : '본문';
        const r = await reconvertFigure(
          'bank', pid, figure.field, Number(figure.index),
          {
            expectedUpdatedAt: figGenOrigins.token || dataRef.current?.updated_at,
            userPrompt, mode: genMode, ...(figure.request || {}),
          },
          getAccessToken,
        );
        if (r === AUTH_REDIRECT) return;
        if (String(targetRef.current) !== String(pid)) return;
        if (r?.converted === false) {
          setToast({
            type: 'info',
            text: figureRedrawRejectedText(where, figure.index, r?.suggest === 'trace'),
            issues: Array.isArray(r.issues) ? r.issues : [],
          });
          return;
        }
        setToast({ type: 'info', text: figureRedrawnText(where, figure.index, genMode) });
        load();
        return;
      }
      if (source === 'upload' && image) {
        const where = field === 'solution' ? '풀이' : '본문';
        const inserted = await insertImageNow(field, image);
        if (!inserted) {
          setToast({ type: 'error', text: `${where} 편집기에 넣지 못했습니다 — 편집 모드에서 다시 시도해 주세요.` });
          return;
        }
        const saved = await handleSave();
        if (!saved) return; // 사유는 handleSave 가 말했다 — 이미지는 편집기에 남아 있어 [저장]으로 이어 갈 수 있다
        const list = await fetchFigures('bank', pid, getAccessToken);
        if (list === AUTH_REDIRECT) return;
        const same = (Array.isArray(list?.items) ? list.items : []).filter((it) => it.field === field && it.src === image);
        const item = same[same.length - 1];
        if (!item) {
          setToast({ type: 'error', text: `${where}에 이미지를 넣고 저장했지만 서버 그림 목록에서 찾지 못했습니다 — 그림을 눌러 [AI 요청]을 쓰세요.` });
          return;
        }
        try {
          const r = await reconvertFigure(
            'bank', pid, field, Number(item.index),
            { expectedUpdatedAt: list?.updated_at, userPrompt, mode: genMode },
            getAccessToken,
          );
          if (r === AUTH_REDIRECT) return;
          if (String(targetRef.current) !== String(pid)) return;
          if (r?.converted === false) {
            setToast({
              type: 'info',
              text: `${where} 끝에 올린 이미지를 그림으로 넣었습니다. AI 가 좌표·식으로 옮기지는 못해 이미지 그대로 둡니다(손실 없음).`,
              issues: Array.isArray(r.issues) ? r.issues : [],
            });
          } else {
            setToast({ type: 'info', text: `${where} 끝에 올린 이미지로 그림을 만들었습니다 — 그림을 눌러 고칠 수 있습니다.` });
          }
        } catch (e) {
          if (String(targetRef.current) !== String(pid)) return;
          setToast({ type: 'error', text: `${where} 끝에 올린 이미지는 그림으로 넣었지만 AI 요청은 실패했습니다 — ${describeFigureError(e).text}` });
        }
        load();
        return;
      }
      const d = await generateFigure(
        'bank', pid,
        { field, userPrompt, mode: genMode, expectedUpdatedAt: dataRef.current?.updated_at },
        getAccessToken,
      );
      if (d === AUTH_REDIRECT) return;
      if (String(targetRef.current) !== String(pid)) return; // 기다리는 동안 문제를 옮겼으면 버린다
      // 계약 §9-2: 모델이 「그림 불필요·확정 불가」로 본 경우 200 + generated:false 다.
      // **실패가 아니다** — 본문은 그대로고 손실이 없다. 오류로 말하면 사용자가 망가졌다고 읽는다.
      if (d?.generated === false) {
        setToast({ type: 'info', text: FIGURE_NOT_GENERATED_TEXT, issues: d.issues });
        return;
      }
      setToast({ type: 'info', text: `${field === 'solution' ? '풀이' : '본문'} 끝에 그림을 만들어 붙였습니다 — 그림을 눌러 고칠 수 있습니다.` });
      // 본문이 서버에서 바뀌었다. 다시 읽지 않으면 편집기가 새 그림을 모르고, 다음 저장이
      // 옛 updated_at 으로 나가 409 가 된다.
      load();
    } catch (e) {
      setToast({ type: 'error', text: describeFigureError(e).text });
    } finally {
      setFigGenBusy(false);
      setFigGenField(null);
    }
  };

  // 편집기가 잠긴 이유. 화면이 「왜 지금 못 고치는가」를 스스로 말해야 사용자가 고장으로 읽지 않는다.
  const figureLockReason = saving
    ? '저장하는 동안에는 고칠 수 없습니다.'
    /* AI 생성이 도는 동안에도 잠근다(2026-09-06 · 검수 모달과 맞춤). 그 호출은 서버 본문에 그림을 붙이고,
       끝나면 화면이 상세를 다시 읽는다(runFigGen 의 load) — 기다리는 동안 고친 글은 그 재조회가 말없이 덮는다.
       검수 모달은 이미 이 구간을 잠그고 있었고(busy 에 figGenBusy 포함), 여기만 열려 있어 같은 조작의 결과가 갈렸다. */
    : figGenBusy
      ? 'AI 가 그림을 만드는 동안에는 고칠 수 없습니다 — 끝나면 본문을 다시 읽습니다.'
      : (figureTarget != null ? '그림 창을 닫으면 다시 고칠 수 있습니다.' : null);

  // Provider 로 내려갈 값. 매 렌더마다 새 객체를 만들면 편집기 안 그림이 전부 다시 그려진다.
  // onPasteImage 를 주면 편집기가 이미지를 받는 순간을 호스트가 받는다(PasteFigurePlugin) — 검수 모달과 같은 배선.
  // onAction 은 **그림 위 버튼 넷을 켜는 스위치**다 — 강사(canEditFigures=false)에게는 주지 않아 막대가 서지 않고
  // 크기 조절만 남는다. onPasteImage 는 그대로 준다: 붙여넣기 판정은 버튼과 무관하다(FigureEditContext 머리주석).
  const figureEditByField = useMemo(() => ({
    body: { field: 'body', svgEditorEnabled: FIGURE_SVG_EDITOR, ...(canEditFigures ? { onAction: handleFigureAction } : {}), onPasteImage: handlePasteImage },
    solution: { field: 'solution', svgEditorEnabled: FIGURE_SVG_EDITOR, ...(canEditFigures ? { onAction: handleFigureAction } : {}), onPasteImage: handlePasteImage },
  }), [handleFigureAction, handlePasteImage, canEditFigures]);
  const figureEditFor = (field) => figureEditByField[field];

  /**
   * [보기|편집] 전환 — 본문·정답·풀이·개념·난이도·라벨을 **함께** 바꾸는 이 칸 전체의 상태다.
   * 두 칸 배치에서는 칸 머리(번호 줄 오른쪽 끝)로 그려 넣는다(modeSlot · 오너 지시 2026-09-13 — 본문 줄에 서면
   * 본문 전용으로 읽혔다). 상태는 여기 둔다: 끌어올리면 전환 직전에 편집분을 보관하는 장치(flushLexBeforeSwitch)까지
   * 옮겨야 한다. 얼굴은 같은 머리 줄의 오른쪽 탭 줄 그대로(pages/ProblemHub tabRow — 12px · 고른 쪽 굵게) —
   * 한 줄에 선 둥근 칸 둘이 크기가 다르면 따로 노는 것으로 읽힌다.
   */
  const modeToggle = (
    <div className="flex shrink-0 overflow-hidden rounded-full bg-gray-100 text-xs" role="group" aria-label="표시 모드">
      {[['view', '보기'], ['edit', '편집']].map(([m, lbl]) => (
        <button
          key={m}
          type="button"
          onClick={() => switchMode(m)}
          aria-pressed={mode === m}
          className={`whitespace-nowrap px-3 py-1 transition-colors ${
            mode === m ? 'bg-primary font-medium text-white' : 'text-gray-500 hover:bg-gray-200'
          }`}
        >
          {lbl}
        </button>
      ))}
    </div>
  );
  // 읽는 동안(저장 뒤 새로 읽기 포함)에도 머리의 전환은 자리를 지킨다 — 본문은 뼈대로 바뀌어도 머리 줄은 그대로다
  const modePortal = modeSlot ? createPortal(modeToggle, modeSlot) : null;

  if (problemId == null) return null;
  if (loading) {
    return (
      <>
        {modePortal}
        <div className="space-y-2 p-1">
          <div className="h-6 w-40 rounded bg-gray-100 motion-safe:animate-pulse" />
          <div className="h-40 w-full rounded-xl bg-gray-100 motion-safe:animate-pulse" />
          <div className="h-10 w-full rounded-xl bg-gray-100 motion-safe:animate-pulse" />
        </div>
      </>
    );
  }
  if (error) {
    return (
      <div className="space-y-2 p-4">
        <p className="text-sm text-danger">{error}</p>
        <Button size="xs" variant="secondary" onClick={load}>다시 시도</Button>
      </div>
    );
  }
  if (!data) return null;

  const origin = getOriginDisplay(data);
  // 두 번호가 다 있을 때만 배지가 눌린다 — null(OCR 출신 아님·OCR 쪽 삭제)과 운영 배포 전 서버(두 칸 없음)는 글자로 남는다
  const ocrPath = canOpenOcr ? ocrReviewPathOf(data) : null;
  const concepts = Array.isArray(data.concept_names) ? data.concept_names : [];
  // 보기도 정본(미저장 초안 포함)을 그린다 — 본문·정답·풀이·라벨과 같은 규칙
  const viewConcepts = conceptDirty ? conceptIds.map((id) => ({ concept_id: id, name: nameOfConcept(id) })) : concepts;
  const shownDifficulty = difficultyDirty && difficulty !== '' ? Number(difficulty) : data.difficulty;
  // 라벨 — 콤마로 이어진 한 줄이거나 배열로 온다(계약 §3 labels_raw). 구역을 세울지 정하려면 개수가 먼저 필요하다.
  const labelTags = splitLabels(data.labels_raw);
  // 라벨 칸 — 편집이면 사전 목록에 초안을 얹은 칩. 보기이면 저장본이되, 미저장 초안이 있으면 그것을 그린다
  // (본문·정답·풀이의 「보기도 정본을 그린다」와 같은 규칙 — 여기만 서버 값을 그리면 화면과 저장될 값이 갈린다).
  const labelChipItems = labelReady ? labelChips(labelItems, labelDraft, data.labels_raw) : [];
  const viewLabelTexts = labelReady && labelDirty ? labelChipItems.map((c) => c.text) : labelTags;
  const commitLabelInput = () => {
    if (!labelReady) return;
    const r = addLabelTokens(labelItems, labelDraft, splitLabels(labelInput));
    setLabelDraft(r.draft);
    // 넣지 못한 것만 입력칸에 남긴다 — 무엇이 왜 안 들어갔는지 보이게
    setLabelInput(r.rejected.map((x) => x.text).join(', '));
    if (r.rejected.length > 0) setToast({ type: 'error', text: describeRejectedLabels(r.rejected) });
  };
  const pickLabel = (it) => {
    if (!labelReady) return;
    const r = addLabelTokens(labelItems, labelDraft, [{ label_uuid: it.label_uuid, label_name: it.label_name }]);
    setLabelDraft(r.draft);
    // 콤마로 여럿을 치던 중이면 앞의 것은 남기고 마지막 조각(찾던 글자)만 지운다
    const parts = labelInput.split(',');
    parts.pop();
    setLabelInput(parts.length > 0 ? `${parts.join(',')},` : '');
  };
  const removeLabel = (key) => setLabelDraft((d) => removeLabelChip(labelItems, d, key));
  const editing = mode === 'edit';
  /**
   * 보기 상자를 **두 번 누르면 편집으로 들어간다**(오너 지시 2026-09-01). 본문·정답·풀이 셋 다.
   *
   * 손이 짧아진다 — 지금은 고칠 곳을 화면에서 찾은 뒤 눈과 손이 위의 [편집]까지 갔다가 그
   * 자리로 되돌아와야 한다. 두 번 누르기는 그 왕복을 없앤다(문서 편집기의 오랜 관례다).
   *
   * **마우스 편의일 뿐이다.** 키보드·보조기기 경로는 위의 [보기|편집] 버튼이 그대로 갖는다 —
   * 이 저장소가 이미 쓰는 짝이다(PaperComposeModal 의 「행 클릭 ↔ 순번 버튼」).
   * 그래서 onDoubleClick 하나만 얹고 role·tabIndex 는 만들지 않는다: 없는 버튼을 보조기기에
   * 알리면 그쪽에서는 **누를 수 없는 것**이 하나 생긴다.
   *
   * 치르는 값 하나 — 보기 상자에서 **낱말을 두 번 눌러 고르던 동작**이 편집 진입으로 바뀐다.
   * 편집 모드에서도 글자는 그대로 고를 수 있으니 잃는 것은 그 한 번의 선택뿐이다.
   *
   * title 을 함께 다는 이유: 두 번 누르기는 화면에 흔적이 없다. 적어 두지 않으면 아무도 모른다.
   * (편집 모드에서는 통째로 null 이다 — 편집기 안에서 두 번 누르는 것은 낱말 고르기다.)
   */
  const enterEditOnDoubleClick = editing ? null : {
    /**
     * 두 번째 눌림의 **기본동작(낱말 고르기)을 막는다**(오너 보고 2026-09-01 「드래그된다」).
     * 안 막으면 편집으로 들어가는 순간 글자가 파랗게 잡힌 채로 시작하는데, 그 선택은 복사하려던
     * 것이 아니라 두 번 누른 부산물이다. `detail` 은 연속 눌림 횟수라 **첫 눌림(1)은 건드리지
     * 않는다** — 끌어서 글자를 고르는 길은 그대로 남는다.
     */
    onMouseDown: (e) => { if (e.detail >= 2) e.preventDefault(); },
    onDoubleClick: (e) => {
      /* 붙들 자리는 **한 칸 바깥**(본문/정답/풀이 한 덩이)이다. 정답은 보기 상자가 통째로
         갈려 사라져 잴 것이 없어지고, 바깥은 모드가 바뀌어도 그대로 남는다.
         상자 안의 글이 아니라 **칸의 윗변**을 붙드는 이유: 글을 붙들면 도구줄 높이만큼
         스크롤을 내리게 되는데, 맨 위에서 두 번 누른 경우 그 바람에 [보기|편집] 줄이
         화면 밖으로 밀려난다. 칸을 붙들면 아무것도 사라지지 않고, 남는 어긋남은 상자 안에서
         도구줄이 밀어낸 40px 남짓뿐이다 — 그건 「도구줄이 생겼다」로 눈에 설명이 된다. */
      const el = e.currentTarget.parentElement;
      if (el) scrollAnchorRef.current = { el, top: el.getBoundingClientRect().top };
      switchMode('edit');
    },
    title: '두 번 누르면 편집으로 바뀝니다',
  };
  const bodyNow = lexNow(bodyEditorRef, bodyLexPendingRef, lexKey('b'), data.body_lexical);
  const solutionNow = lexNow(solutionEditorRef, solutionLexPendingRef, lexKey('s'), data.solution_lexical);

  return (
    <div className="space-y-4">
      {modePortal}
      {/* 라이브 리전은 항상 마운트한다 — 비어 있을 때는 sr-only 로 물러난다 */}
      <p
        role={toast?.type === 'error' ? 'alert' : 'status'}
        aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
        className={`text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
      >
        {toast?.text || ''}
      </p>
      {/* 서버가 쓴 사유 줄들 — **그대로 낸다.** 무엇이 왜 걸렸는지는 서버만 안다(그림 창과 같은 규칙).
          지금 이것을 싣는 유일한 자리가 그림 생성의 `generated:false` 다(계약 §9-2). */}
      {/* 붉게 낸다(오너 지시 2026-09-01) — 그림 창의 같은 목록과 한 벌이다(FigureModal).
          위 토스트 문장은 회색 그대로다: 「본문은 그대로다」는 안심시키는 말이고 여기 줄들은
          「이래서 못 만들었다」라, 한 색으로 묶으면 사유가 안내문에 묻힌다. */}
      {toast?.issues?.length > 0 && (
        <ul className="list-disc space-y-0.5 rounded-lg bg-gray-50 py-2 pl-6 pr-3 text-[11px] text-danger">
          {toast.issues.map((t, i) => <li key={`${i}-${String(t).slice(0, 12)}`}>{String(t)}</li>)}
        </ul>
      )}

      {stale && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <span className="min-w-0 flex-1">
            {/* "다른 사람"만이 아니다 — 분석 승인·본문 되돌리기도 이 잠금을 돌린다(계약 §3).
                범인을 사람으로만 적어 두면, 혼자 쓰는 사용자가 자기 화면을 고장으로 읽는다. */}
            내가 읽은 뒤 이 문제가 바뀌었습니다 — 다른 사람의 저장이거나, [분석]의 승인 또는
            [수정 이력]의 되돌리기입니다. 지금 저장하면 그 수정을 덮게 되어 막았습니다.
            내 편집은 화면에 그대로 두었습니다 — 지킬 내용이 있으면 먼저 옮겨 적은 뒤 [새로 읽기]로 최신을 받아 오세요.
          </span>
          <Button
            size="xs"
            variant="secondary"
            disabled={busy}
            onClick={() => { discardEdits(); load(); }}
          >
            새로 읽기(내 편집 버림)
          </Button>
        </div>
      )}

      {/* 머리 — 출신·난이도·작성자. 라벨은 여기 없다 — 아래 [개념] 다음 제 구역에 선다. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {origin && (ocrPath ? (
          /* 얼굴은 그대로 두고 밑줄만 더한다(오너 결정 2026-10-02). 누르면 페이지를 옮기지 않고 검수 모달을 이 자리에
             띄운다(오너 지시 같은 날 「모달만」). 진짜 <a> 로 두는 이유는 꾸밈 클릭(⌘·Ctrl) — 그때는 브라우저가
             OCR 검수 화면 주소를 새 탭으로 연다(ProblemIdLink 와 같은 판정). */
          <Link
            to={ocrPath}
            onClick={(e) => {
              if (isModifiedClick(e)) return;
              e.preventDefault();
              setOcrReviewOpen(true);
            }}
            aria-label={`${origin.text} — OCR 프로젝트 #${data.ocr_project_id} 의 검수 창 열기`}
            title={`OCR 프로젝트 #${data.ocr_project_id} 의 검수 창을 엽니다 — ⌘·Ctrl 을 누르고 선택하면 OCR 화면을 새 탭으로 엽니다`}
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium underline-offset-2 hover:underline ${origin.cls}`}
          >
            {origin.text}
          </Link>
        ) : (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${origin.cls}`}>{origin.text}</span>
        ))}
        {ocrReviewOpen && ocrPath && (
          <OcrSourceReviewModal
            projectId={data.ocr_project_id}
            ocrProbId={data.ocr_prob_id}
            onClose={() => setOcrReviewOpen(false)}
          />
        )}
        {data.origin_parent_id != null && (
          /* 원본 번호 — 칩을 걷고 정본 얼굴(ProblemIdLink)로 세운다(오너 지시 2026-09-11 「문제번호 디자인 그대로」).
             칩 안에 12px 번호를 넣으면 10px 알약이 두 목소리로 말하므로(ProblemIdLink 의 chip 주석) 알약째 걷고,
             「원본」은 곁의 작성자와 같은 흐린 11px 글자로 둔다. 크기가 다른 두 글자라 기준선으로 맞춘다.
             이동은 링크의 기본 길(/problem-hub/problem/:id)과 페이지의 이동 가드(ProblemNavGuard)에 맡긴다 —
             onOpen 으로 onOpenProblem 을 또 부르면 미저장 편집이 있을 때 확인창이 두 번 뜬다(가드 한 번 + 이동 함수 한 번). */
          <span className="inline-flex items-baseline gap-1 text-[11px] text-gray-400">
            원본
            <ProblemIdLink id={data.origin_parent_id} item={originItem} linked={!!onOpenProblem} />
          </span>
        )}
        {editing && canEditMeta ? (
          /* 편집이면 배지 자리가 고르는 칸이 된다(오너 결정 2026-09-13 A — 보기에서 난이도가 서는 바로 그 자리).
             얼굴은 같은 화면 [분석] 카드의 난이도 칸 그대로(AnalysisReviewCard — 제목 12px/500 회색 + text-sm select).
             비우기가 없어(null 은 400) 값이 있는 문제에는 「없음」을 두지 않는다. 값이 없는 문제의 「없음」은 「그대로 둠」이다. */
          <label className="flex items-center gap-1.5 text-sm text-gray-600">
            <span className="text-xs font-medium text-gray-500">난이도</span>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              disabled={busy}
              aria-label="난이도"
              className="rounded-lg border px-2 py-1 text-sm disabled:bg-gray-50"
            >
              {data.difficulty == null && <option value="">없음</option>}
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{difficultyOptionLabel(n)}</option>)}
            </select>
          </label>
        ) : shownDifficulty != null && (
          /* 색은 공용 값(problemApi.difficultyBadgeClass — 1 노랑·2 초록·3 파랑·4 주황·5 빨강)이다. 목록 표·카드·
             시험지 담기 창과 같은 난이도가 같은 색이어야 한다. 크기는 이 줄의 알약(출신 배지)과 같은 10px 로 둔다. */
          <span className={`rounded-full px-2 py-0.5 text-[10px] ${difficultyBadgeClass(shownDifficulty)}`}>
            난이도 {shownDifficulty}{DIFFICULTY_LABEL[shownDifficulty] ? ` (${DIFFICULTY_LABEL[shownDifficulty]})` : ''}
          </span>
        )}
        {data.author && <span className="text-[11px] text-gray-400">{data.author}</span>}
      </div>

      {/* 개념 — 편집이면 공용 개념 고르기(ConceptPicker · 오너 결정 2026-09-13 D4), 보기이면 배지 */}
      <div>
        <div className="mb-1 text-xs font-medium text-gray-500">개념</div>
        {editing && canEditMeta ? (
          <>
            <ConceptPicker
              value={conceptIds}
              onChange={setConceptIds}
              busy={busy}
              resetKey={data.problem_id}
              onChipClick={openConcept}
              names={nameOfConcept}
              excludeOutdated
              listInFlow /* 스크롤 칸 안이고 바로 아래 라벨 칸이 붙는다 — 띄우면 그 밑에 깔린다(LabelEditor 주석의 실측) */
              emptyText="개념이 없습니다 — 하나 이상 고르세요"
            />
            {outdatedPicked.length > 0 && (
              <p className="mt-1 text-xs text-amber-600">
                폐기된 개념: <MathName text={outdatedText(outdatedPicked)} compact /> — 개념을 고쳐 저장하려면 먼저 떼세요.
              </p>
            )}
          </>
        ) : viewConcepts.length > 0 ? (
          // 배지 모양·크기는 시험지 통계 열(PaperStatsPanel)과 **같은 것**을 쓴다(오너 지시
          // 2026-08-29) — [순번][유형] 두 칸 + 이름. 누르면 그 개념의 설명이 뜬다.
          <div className="flex flex-wrap gap-1.5">
            {viewConcepts.map((c) => {
              const mc = matchConcept(conceptDict, c.concept_id);
              return (
                <ConceptBadge
                  key={c.concept_id ?? c.name}
                  name={c.name}
                  order={mc?.order ?? null}
                  type={mc?.concept_type ?? null}
                  compact
                  onClick={c.concept_id != null ? () => openConcept(c.concept_id) : null}
                />
              );
            })}
          </div>
        ) : (
          <span className="text-xs text-gray-400">
            {canEditMeta
              ? '배정된 개념이 없습니다 — [편집]에서 고를 수 있습니다.'
              : '배정된 개념이 없습니다 — [분석] 탭에서 AI로 채울 수 있습니다.'}
          </span>
        )}
        {editing && !canEditMeta && (
          /* 옛 서버(279bbed 전) — 두 키를 실으면 저장이 통째로 400 이라 칸을 세우지 않는다(오너 결정 B · 머리주석) */
          <p className="mt-1 text-[11px] text-gray-400">
            서버 준비 전이라 개념·난이도는 아직 여기서 고칠 수 없습니다 — [분석] 탭을 쓰세요.
          </p>
        )}
      </div>

      {/* 라벨 — **[개념] 바로 아래 제 구역**(오너 지시 2026-09-02). 종전에는 맨 위 머리줄에
          출신·난이도·작성자와 섞여 섰다 — 9px 꼬리표가 10px 알약 사이에 끼어 어느 것이 라벨인지
          눈으로 못 갈랐고, 문제를 고르는 단서 둘(개념·라벨)이 머리줄과 [개념] 구역으로 갈라져 있었다.
          목록 카드(ProblemSearchPanel)·조립 창(PaperComposeModal)이 개념 아래에 라벨을 두는 것과
          같은 순서다. 구역의 제목·간격은 바로 위 [개념] 구역 그대로(mb-1 제목 · gap-1.5).
          배지는 표·카드·조립 창과 **같은 부품**(ExamPapersV2/labelBadge · 오너 지시 2026-08-29 규격).
          보기에서 값이 없으면 구역째 없다. 편집에서는 늘 선다 — 2026-09-13 부터 여기서 라벨을 붙이고 뗀다
          (LabelEditor — 검수 창과 같은 얼굴 · 사전에서 찾아 고르거나 새 이름을 친다). */}
      {(editing || viewLabelTexts.length > 0) && (
        <div>
          <div className="mb-1 text-xs font-medium text-gray-500">라벨</div>
          {!editing ? (
            <LabelBadges labels={viewLabelTexts} className="flex flex-wrap gap-1.5" />
          ) : labelServer.status === 'error' && !labelReady ? (
            /* 목록을 못 읽으면 칸을 세우지 않는다 — 번호 없이는 뗄 수 없고, 빈 칸은 「라벨 없음」으로 읽힌다.
               얼굴은 ConceptPicker 의 「개념 사전을 불러오지 못했습니다 · 다시 시도」와 같다. */
            <p className="flex items-center gap-1.5 text-xs text-amber-600">
              라벨을 불러오지 못했습니다.
              <button type="button" onClick={loadLabels} className="underline hover:text-amber-700">
                다시 시도
              </button>
            </p>
          ) : (
            <LabelEditor
              items={labelChipItems}
              onRemove={removeLabel}
              inputValue={labelInput}
              onInputChange={setLabelInput}
              onCommit={commitLabelInput}
              suggest={suggestLabels}
              onPick={pickLabel}
              busy={busy || !labelReady}
              emptyText={labelReady ? '없음' : '라벨을 읽는 중…'}
              placeholder="라벨 추가 — 사전에서 찾아 고르거나 새 이름을 치고 Enter (콤마로 여럿)"
            />
          )}
        </div>
      )}

      {/* 본문 */}
      <div>
        {/* [미리보기]가 이 줄에 선다(오너 지시 2026-08-29). 모드 전환은 두 칸 배치에서 칸 머리로 올라갔다
            (2026-09-13 — modeToggle 주석). 머리 자리를 받지 못한 한 단 배치에서만 종전대로 이 줄에 선다 —
            한 단에서는 [문제 보기]가 여러 탭 중 하나라 머리에 두면 다른 탭을 보는 동안에도 남는다.
            미리보기는 ml-auto 로 우측 끝에 붙인다. */}
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">본문</span>
          {!modeSlot && modeToggle}
          {dirty && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">수정됨 · 미저장</span>
          )}
          {editing && <span className="text-[10px] text-gray-400">고치면 [수정 이력]에 남아 되돌릴 수 있습니다</span>}
          {/* AI 생성 — 그림이 **없는** 본문에도 뜬다. 그것이 이 버튼의 이유다(계약 §9-2).
              있는 그림을 고치는 넷(편집·AI 요청·그림 교체·지우기)은 그림을 눌렀을 때 그림 위에 뜬다. */}
          {/* 얼굴은 **흰 바탕에 초록 테두리**(primary-outline · 오너 지시 2026-08-31).
              회색 채움(secondary)일 때는 곁의 잠긴 버튼과 같은 회색이라, 이 화면에서 유일하게
              무언가를 새로 만드는 조작이 「눌러도 되나」로 읽혔다. 공용 Button 에 이미 있는
              변형이라 색을 새로 짜지 않는다. */}
          {/* 검수 모달과 같은 입구·같은 창(FigureAddModal) — AI 생성 / 이미지 파일 추가(2026-09-05 통일).
              미저장이어도 막지 않는다: AI 생성은 openFigGen 이 먼저 저장하고, 파일은 편집기에 들어간다.
              강사에게는 버튼째 없다(canRunAi · 2026-09-10) — 사유는 이 부품의 머리주석. */}
          {canRunAi && (
            <Button
              size="xs"
              variant="primary-outline"
              onClick={() => setFigAddField('body')}
              disabled={busy}
              title="본문에 그림을 넣습니다 — AI 로 만들거나 이미지 파일을 고릅니다"
              icon={<ImagePlus className="size-3.5" />}
            >
              이미지 추가
            </Button>
          )}
          <span className="ml-auto" />
          {/* 미리보기는 서버에 저장된 내용을 받아 그린다 — 미저장 편집분은 비치지 않는다.
              열고 나서 "왜 내 수정이 없지"가 되지 않게 열기 전에 말해 둔다. */}
          {dirty && <span className="text-[10px] text-amber-600">미저장 편집은 미리보기에 나오지 않습니다</span>}
          <Button
            size="xs"
            variant="secondary"
            onClick={() => setPreviewOpen(true)}
            icon={<Eye className="size-3.5" />}
            title="저장된 내용을 지면 모양으로 봅니다"
          >
            미리보기
          </Button>
        </div>
        {bodyNow ? (
          <div className="rounded-xl border border-gray-200" {...enterEditOnDoubleClick}>
            {editing ? (
              <OcrLexicalEditor
                key={lexKey('b')}
                /* 모드 전환으로 언마운트됐던 편집분이 있으면 그것으로 복원(없으면 서버 값) */
                initialState={
                  bodyLexPendingRef.current?.key === lexKey('b') && bodyLexPendingRef.current?.dirty
                    ? bodyLexPendingRef.current.state
                    : data.body_lexical
                }
                fallbackState={data.body_lexical} /* 스냅샷이 깨졌으면 서버 값으로 */
                baselineState={data.body_lexical} /* dirty 기준은 항상 서버 값 */
                label="본문"
                stateRef={bodyEditorRef}
                stateKey={lexKey('b')} /* 이 편집기가 어느 문제의 것인지 — readLexEdit 가 대조한다 */
                onDirtyChange={setBodyDirty}
                onUnmount={(snap) => keepLexSnapshot('본문', snap, bodyLexPendingRef, lexKey('b'), setBodyDirty)}
                /* 잠그는 것은 저장하는 동안·AI 생성이 도는 동안·그림 창이 열려 있는 동안이다 — 앞의 둘은
                   끝나고 화면이 상세를 다시 읽어 그 사이의 편집을 덮고, 그림 창은 서버에 바로 쓰는데
                   순번이 저장본 기준이라 뒤에서 본문이 바뀌면 다른 그림에 쓰게 된다(사유는 figureLockReason) */
                readOnly={saving || figGenBusy || figureTarget != null}
                readOnlyReason={figureLockReason}
                figureEdit={figureEditFor('body')}
              />
            ) : (
              /* 보기도 정본(미저장 편집분 포함)을 그린다. key 에 'v' 를 더해 편집기 인스턴스와
                 섞이지 않게 한다(같은 key 면 모드 전환에 리마운트가 안 된다). */
              <OcrLexicalEditor
                key={`${lexKey('b')}|v`}
                initialState={bodyNow}
                fallbackState={data.body_lexical}
                label="본문"
                readOnly
              />
            )}
          </div>
        ) : (
          <p className="rounded-xl border border-gray-100 p-3 text-sm text-gray-400">본문이 없습니다.</p>
        )}
      </div>

      {/* 정답 */}
      <div>
        <div className="mb-1 text-xs font-medium text-gray-500">정답</div>
        {editing ? (
          <>
            {/* 객관식 빠른 입력 — 문제은행 관례가 '①'~'⑤' 원문자 문자열 그대로다(AnswerModal).
                자유 입력만 두면 "3"/"③" 두 표기가 섞여 쌓인다. 상세 계약(§3)에 객관식 여부가
                없어 항상 보인다 — 주관식 문제에서는 그냥 안 누르면 된다.
                ⚠ 2026-08-26 계약 §4 D2: **AI 변형 승인분도 이제 `'3'` 이 아니라 `'③'` 으로
                저장된다**(승격·분석 v2 와 같은 표기). 즉 이 화면이 쓰던 관례가 은행 전체의
                관례가 됐다 — 여기서 숫자를 기대하도록 되돌리면 안 된다.
                다만 **그 이전에 쌓인 문제는 여전히 '3' 일 수 있다.** 아래 비교(`=== mark`)는
                원문자로만 맞으므로 옛 표기의 문제에서는 어느 동그라미도 눌린 것으로 보이지 않는다.
                그것을 원문자로 자동 치환하지 않는 이유는, 정답을 화면이 말없이 고쳐 쓰는 순간
                "내가 고치지 않은 값이 저장되는" 일이 되기 때문이다 — 사용자가 누르면 바뀐다. */}
            <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-1">
              {CIRCLED.slice(0, 5).map((mark) => (
                <button
                  key={mark}
                  type="button"
                  onClick={() => setAnswerText(answerText.trim() === mark ? '' : mark)}
                  disabled={busy}
                  /* 선택 표시가 색·굵기뿐이라 눌린 상태가 프로그램적으로 안 드러난다 */
                  aria-pressed={answerText.trim() === mark}
                  title={`정답을 ${mark}로 (다시 누르면 해제)`}
                  className={`size-8 rounded-full border text-sm transition-colors disabled:opacity-40 ${
                    answerText.trim() === mark
                      ? 'border-primary bg-primary font-bold text-white'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-primary/50'
                  }`}
                >
                  {mark}
                </button>
              ))}
            </div>
            <AnswerField
              value={answerText}
              onChange={setAnswerText}
              editing={answerEditing}
              setEditing={setAnswerEditing}
              disabled={busy}
            />
          </>
        ) : (
          // 보기도 정본을 그린다 — 본문·풀이와 같은 규칙이다. 여기만 서버 값을 그리면
          // 고쳐 놓고 보기로 넘어왔을 때 화면과 저장될 값이 갈린다.
          <div className="rounded-xl border border-gray-100 px-3 py-2" {...enterEditOnDoubleClick}>
            <AnswerView value={answerText} />
          </div>
        )}
      </div>

      {/* 풀이 */}
      <div>
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-gray-500">풀이</span>
          {/* 풀이가 **있을 때만** 낸다 — 계약 §9-2 는 본문이 없으면 409 로 거절한다.
              눌리면 반드시 실패하는 버튼을 내지 않는다는 이 저장소의 규칙(figureCapabilities).
              강사에게는 본문 쪽과 같이 버튼째 없다(canRunAi). */}
          {solutionNow && canRunAi && (
            <Button
              size="xs"
              variant="primary-outline"
              onClick={() => setFigAddField('solution')}
              disabled={busy}
              title="풀이에 그림을 넣습니다 — AI 로 만들거나 이미지 파일을 고릅니다"
              icon={<ImagePlus className="size-3.5" />}
            >
              이미지 추가
            </Button>
          )}
        </div>
        {solutionNow || (editing && solutionDraft) ? (
          <div className="rounded-xl border border-gray-200" {...enterEditOnDoubleClick}>
            {editing ? (
              <OcrLexicalEditor
                key={lexKey('s')}
                initialState={
                  solutionLexPendingRef.current?.key === lexKey('s') && solutionLexPendingRef.current?.dirty
                    ? solutionLexPendingRef.current.state
                    : (data.solution_lexical ?? EMPTY_LEXICAL)
                }
                fallbackState={data.solution_lexical ?? EMPTY_LEXICAL}
                baselineState={data.solution_lexical ?? EMPTY_LEXICAL}
                label="풀이"
                stateRef={solutionEditorRef}
                stateKey={lexKey('s')} /* 위와 같음 — 남의 문제 편집기를 정본으로 읽지 않게 */
                onDirtyChange={setSolDirty}
                onUnmount={(snap) => keepLexSnapshot('풀이', snap, solutionLexPendingRef, lexKey('s'), setSolDirty)}
                readOnly={saving || figGenBusy || figureTarget != null}
                readOnlyReason={figureLockReason}
                figureEdit={figureEditFor('solution')}
              />
            ) : (
              <OcrLexicalEditor
                key={`${lexKey('s')}|v`}
                initialState={solutionNow}
                fallbackState={data.solution_lexical}
                label="풀이"
                readOnly
              />
            )}
          </div>
        ) : data.has_solution ? (
          // 계약(§3)이 has_solution 을 따로 주는 이상 둘이 갈릴 수 있다 — "없음"으로 뭉뚱그리면
          // 좌측 '풀이 있음' 필터로 걸러 온 문제가 여기서 없다고 나오는 모순이 된다.
          <p className="rounded-xl border border-amber-100 bg-amber-50 p-3 text-sm text-amber-700">
            풀이가 있지만 이 화면으로 열 수 있는 형태가 아닙니다.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 p-3">
            <span className="text-sm text-gray-400">풀이가 없습니다.</span>
            {editing && (
              // 계약(§B-1)이 solution_lexical 을 upsert 한다 — 없는 풀이도 여기서 처음 쓸 수 있다.
              <Button size="xs" variant="secondary" onClick={() => setSolutionDraft(true)} disabled={busy}>
                풀이 작성
              </Button>
            )}
          </div>
        )}
      </div>

      {/* 액션 — 편집 중이거나, 보기로 돌아왔는데 미저장 편집이 남아 있을 때.
          sticky: 본문이 길면 저장 단추를 찾으러 끝까지 내려가야 한다. */}
      {(editing || dirty) && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center gap-2 border-t border-gray-100 bg-white py-2">
          {stale ? (
            <span className="text-[11px] text-amber-700">최신을 받기 전에는 저장할 수 없습니다.</span>
          ) : dirty ? (
            <span className="text-[11px] text-gray-500">저장하면 문제은행에 바로 반영됩니다.</span>
          ) : (
            <span className="text-[11px] text-gray-400">고칠 곳을 눌러 수정하세요.</span>
          )}
          <span className="ml-auto" />
          <Button
            size="sm"
            variant="secondary"
            onClick={discardEdits}
            disabled={busy || !dirty}
            icon={<RotateCcw className="size-3.5" />}
          >
            편집 취소
          </Button>
          {/* 409 뒤에는 잠근다 — 같은 expected_updated_at 으로 다시 눌러 봐야 또 막힐 뿐이고,
              누를 수 있게 두면 "몇 번 누르면 되겠지"가 된다. 푸는 길은 [새로 읽기] 하나다. */}
          <Button
            size="sm"
            variant="primary"
            onClick={() => handleSave()}
            loading={saving}
            disabled={busy || !dirty || stale}
            title={stale ? '내가 읽은 뒤 이 문제가 바뀌었습니다 — [새로 읽기]로 최신을 받아야 저장할 수 있습니다' : undefined}
            icon={<Save className="size-3.5" />}
          >
            저장
          </Button>
        </div>
      )}

      {/* 지면 모양 미리보기 — 자기 포털(9999960)을 쓴다. 열어 볼 때만 마운트해 lazy 청크를
          그때 받는다(이 탭을 여는 것만으로 KaTeX·인쇄 렌더러를 끌고 오지 않게). */}
      {/* 그림 창 — 편집 · AI 요청 · 그림 교체(이력) · 지우기.
          닫으면 상세를 다시 읽는다(closeFigure). */}
      {figureTarget && data?.problem_id != null && (
        <FigureModal
          open
          target="bank"
          problemId={data.problem_id}
          field={figureTarget.field}
          index={figureTarget.index}
          nodeSrc={figureTarget.src}
          nodeList={figureTarget.nodes}
          action={figureTarget.action}
          getAccessToken={getAccessToken}
          onClose={closeFigure}
        />
      )}

      {previewOpen && (
        <Suspense fallback={null}>
          <ProblemPreviewV2
            isOpen
            onClose={() => setPreviewOpen(false)}
            problemIds={[data.problem_id]}
            /* 한 건짜리 미리보기 — 번호를 켜면 「01」이 붙어 1번 문항으로 읽힌다 */
            showNumbers={false}
          />
        </Suspense>
      )}
      {/* AI 생성 — 계약 §9-2. **창을 열어 둔 채 기다린다** — 만들 그림이 아직 없어
          진행 표시를 넘길 자리가 없기 때문이다. AiRunModal 이 busy 동안 ESC·[×]·배경을 다 막아
          응답을 놓치지 않는다. */}
      <FigureReconvertModal
        /* canRunAi 를 창에도 건다 — 여는 버튼만 감추면 창 자체는 살아 있다([변형] 탭과 같은 이유). */
        isOpen={figGenField != null && canRunAi}
        kind="generate"
        target="bank"
        field={figGenField || 'body'}
        /* 이 자리 그림들의 원본 카드(2026-09-06 A 안) — 고르면 그 그림을 그 자리에서 다시 그린다(runFigGen 'figure'). */
        figureOrigins={figGenOrigins.items}
        figureOriginsLoading={figGenOrigins.loading}
        busy={figGenBusy}
        onConfirm={(payload) => runFigGen(payload)}
        onClose={() => setFigGenField(null)}
      />

      {/* [이미지 추가]의 갈래 고르기 — 검수 모달과 같은 창. 은행은 「저장된 그림에서 고르기」가 없다
          (본문이 곧 목록이라 서버에만 있는 그림이 없다 — FigureAddModal 머리주석). */}
      <FigureAddModal
        isOpen={figAddField != null && canRunAi}
        target="bank"
        field={figAddField || 'body'}
        onClose={() => setFigAddField(null)}
        onChoose={(kind) => {
          const f = figAddField;
          if (kind === 'generate') openFigGen(f);
          else if (kind === 'file') openImagePicker(f);
        }}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => { takeImageFile(e.target.files?.[0]); e.target.value = ''; }}
      />

      {/* 붙여넣은 이미지를 무엇으로 삼을지 묻는 창 — 바꿀 그림이 있을 때만 뜬다(takeIncomingImage).
          검수 모달과 같은 창이고 target 만 'bank' 다(계약 §10-2 G1 · 2026-09-05). */}
      {pasteTarget && data?.problem_id != null && (
        <OcrPasteImageModal
          isOpen
          target="bank"
          problemId={data.problem_id}
          pasted={pasteTarget}
          getAccessToken={getAccessToken}
          onBeforeReplace={async () => {
            // 교체는 서버 쓰기다 — 저장하지 않은 편집이 있으면 먼저 저장한다(그림 창과 같은 규칙).
            if (!hasUnsaved()) return { ok: true, saved: false };
            const ok = await handleSave();
            return ok ? { ok: true, saved: true } : { ok: false, text: '저장하지 못했습니다 — 화면의 안내를 확인한 뒤 다시 시도해 주세요.' };
          }}
          onClose={() => setPasteTarget(null)}
          onDone={(r) => {
            const t = pasteTarget;
            setPasteTarget(null);
            if (r?.plain) { insertAndTell(t.field, t.src); return; }
            const w = t.field === 'solution' ? '풀이' : '본문';
            setToast({ type: 'info', text: `${w} ${Number(r.index) + 1}번째 그림을 바꿨습니다.` });
            load(); // 서버가 그 노드를 갈아 끼웠다 — 다시 읽어야 편집기가 새 그림을 안다
          }}
        />
      )}

      {/* 개념 설명 — 배지를 누르면 뜬다. data 를 주면 모달이 자체 조회(v1)를 건너뛴다.
          이 화면이 v2 전용인 근거다(오너 방침 2026-08-26 — v1 사전을 끌어오지 않는다). */}
      {conceptDetail && (
        <MathceptDetailModal
          isOpen
          onClose={() => setConceptDetail(null)}
          data={conceptDetail.data}
          loading={conceptDetail.loading}
        />
      )}
    </div>
  );
}

export default ProblemViewTab;
